import { onMount, onCleanup, createSignal, For, Show, createEffect, createMemo, createSelector, untrack, batch } from "solid-js";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import { useAssistantPageScope } from "../../../../hooks/useAssistantPageScope";

import AdvancedScatter from "../../../../components/charts/AdvancedScatter";
import PerformanceChartViewportSlot from "../../../../components/performance/PerformanceChartViewportSlot";
import BoxPlot from "../../../../components/charts/BoxPlot";
import ScatterTimeseries from "../../../../components/charts/ScatterTimeseries";

import Legend from "../../../../components/legends/Performance";
import PerformanceFilterSummary from "../../../../components/legends/PerformanceFilterSummary";
import Loading from "../../../../components/utilities/Loading";
import DropDownButton from "../../../../components/buttons/DropDownButton";
import PerfSettings from "../../../../components/menus/PerfSettings";
import { buildPerformanceBuilderUrl } from "../../../../utils/builderConstants";

import { groupBy, getData, getEventTimes, setupMediaContainerScaling, cleanQuotes } from "../../../../utils/global";
import { runEventTimesSelectionSync } from "../../../../utils/eventTimesSelection";
import { tooltip } from "../../../../store/globalStore";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../utils/logging";
import { log, debug as logDebug, error as logError, warn as logWarn } from "../../../../utils/console";
import { cutEvents, setSelection, selection, clearSelection, cutSelection, hideSelectedEvents, hasSelection, setHasSelection, setSelectedRanges, selectedRanges, setTriggerSelection, selectedAggregateEvents, setSelectedAggregateEvents } from "../../../../store/selectionStore";
import { buildSelectedRangesForEvents, selectionRangesSignatureForEvents, createPerformanceRangeColorResolver, applyCutToRows } from "../../../../utils/selectionRangeHelpers";
import { useDashboardShareRestore } from "../../../../hooks/useDashboardShareRestore";
import { buildPerformanceShareState, applyPerformanceShareState } from "../../../../utils/reportShareState";
import { setStartDate, setEndDate, selectedRacesAggregates, selectedLegsAggregates, selectedStatesAggregates, selectedGradesAggregates, setSelectedStatesAggregates, setSelectedGradesAggregates, setSelectedRacesAggregates, setSelectedLegsAggregates, selectedRacesTimeseries, selectedLegsTimeseries, isTrainingHourMode } from "../../../../store/filterStore";
import { createFilterConfig, passesBasicFilters } from "../../../../utils/filterCore";
import { passesManeuverTrainingRacingFilter } from "../../../../utils/maneuverRaceFilter";

import { performanceDataService } from "../../../../services/performanceDataService";
import { persistantStore } from "../../../../store/persistantStore";
import { apiEndpoints } from "@config/env";
import { user } from "../../../../store/userStore";
import { persistentSettingsService } from "../../../../services/persistentSettingsService";
import { getProjectPerformanceFilters } from "../../../../services/projectFiltersService";
import { appendCrewCodeToFilters } from "../../../../utils/crewFilter";
import TargetSelectionModal from "../../../../components/modals/TargetSelectionModal";
import BaselineDisplayModal from "../../../../components/modals/BaselineDisplayModal";
import { useBaselineDisplay } from "../../../../hooks/useBaselineDisplay";
import { useBaselineExcelExport } from "../../../../hooks/useBaselineExcelExport";
import type { BaselineOverlay } from "../../../../utils/baselineCurve";
import type { BaselineLegendEntry } from "../../../../components/legends/Performance";
import { DEFAULT_PERFORMANCE_TARGET_COLOR } from "../../../../utils/performanceTargetColor";
import {
  loadPerformanceTargetColorFromPersistentSettings,
  readPerformanceTargetSession,
  savePerformanceTargetToPersistentSettings,
  writePerformanceTargetSession,
} from "../../../../utils/performanceTargetPersistence";
import UnifiedFilterService from "../../../../services/unifiedFilterService";
import { finalizePerformanceColorOptions, getColorByIndex, isPerformanceRampColor, resolveDataField } from "../../../../utils/colorScale";
import { performanceBoxPlotGroupField } from "../../../../utils/performanceColorField";
import { assignLocalPerformanceSelection, setPerformanceSelectionsHotkeysActive } from "../../../../utils/performanceLocalSelection";
import { createPerformanceChartsScrollRestore } from "../../../../utils/performanceChartsScrollRestore";
import { forcePerformanceChartUpdate } from "../../../../utils/performanceChartUpdate";
import {
  BOXPLOT_TWS_FIELD,
  sliceAggregatesForBoxPlotWithTws,
} from "../../../../utils/sliceAggregatesForBoxPlot";
import {
  timeWindow,
  getDisplayWindowReferenceTime,
  exploreMapMountedCount,
  isPlaying,
} from "../../../../store/playbackStore";
import {
  applyMapTimeScopeIfActive,
  computeExploreMapReportActiveTimeWindow,
  fleetActiveTimeWindowScopeKey,
  fleetMapRaceLegFilterSignature,
  fleetReportRaceSelectionsFromMap,
  mapReportScopeSignature,
  quantizeFleetReferenceTime,
  readFleetMapRaceLegFilters,
  type MapScopeContext,
  isExploreMapActiveForReports,
} from "../../../../utils/fleetTimeWindow";
import { defaultChannelsStore } from "../../../../store/defaultChannelsStore";
import type {
  PerformanceAggregatePoint,
  PerformanceCloudPoint,
  PerformanceTargetData
} from "../../../../store/dataTypes";
const { selectedClassName, selectedProjectId, selectedDatasetId, selectedDate } = persistantStore;

interface PerformanceBoxPlotChartProps {
  group: { charts: Array<{ filters: unknown }> };
  series: {
    yaxis: { name: string; dataField?: string };
    yType?: string;
    xaxisValue?: string;
    info_type?: string;
    info_message?: string;
  };
  boxPlotDataKey: () => string;
  color: () => string;
  groups: () => Array<{ name: string; color: string }>;
  xAxis: () => string;
  getFilteredAggregates: () => PerformanceAggregatePoint[];
  getFilteredAggregatesAVG: () => PerformanceAggregatePoint[];
  selectedEvents: () => string[];
  handleZoom: (chart: string[]) => void;
  zoom: () => boolean;
}

function PerformanceBoxPlotChart(props: PerformanceBoxPlotChartProps) {
  const chartConfig = createMemo(() => {
    const dataKey = props.boxPlotDataKey();
    const filteredData = props.getFilteredAggregates();
    const currentColor = props.color();
    const currentGroups = props.groups();
    const currentYType = props.series.yType || 'AVG';
    const currentXAxis = props.series.xaxisValue || props.xAxis();

    return {
      _dataSignature: dataKey,
      series: [{
        xaxis: { name: currentColor },
        yaxis: {
          name: props.series.yaxis.name,
          dataField: props.series.yaxis.dataField || props.series.yaxis.name
        },
        aggregate: currentYType,
        originalData: sliceAggregatesForBoxPlotWithTws(
          filteredData,
          props.selectedEvents(),
          props.getFilteredAggregatesAVG(),
          currentXAxis
        ),
        groupField: performanceBoxPlotGroupField(currentColor),
        groups: currentGroups,
        useAbsolute: false,
        useReverse: false,
        yType: currentYType,
        stripTwsField: BOXPLOT_TWS_FIELD
      }],
      filters: props.group.charts[0].filters
    };
  });

  const boxPlotData = createMemo(() =>
    sliceAggregatesForBoxPlotWithTws(
      props.getFilteredAggregates(),
      props.selectedEvents(),
      props.getFilteredAggregatesAVG(),
      props.series.xaxisValue || props.xAxis()
    )
  );

  return (
    <BoxPlot
      chart={chartConfig()}
      data={boxPlotData()}
      handleZoom={props.handleZoom}
      zoom={props.zoom()}
      infoType={props.series.info_type}
      infoMessage={props.series.info_message ?? ""}
    />
  );
}

interface DatasetReportSplitViewProps {
  isInSplitView?: boolean;
  mainPanelMenu?: string | null;
  rightPanelMenu?: string | null;
}

export default function PerformancePage(props: DatasetReportSplitViewProps = {}) {
  // Local state for performance data - separate by aggregate type
  const [aggregatesAVG, setAggregatesAVG] = createSignal<PerformanceAggregatePoint[]>([]);
  const [aggregatesSTD, setAggregatesSTD] = createSignal<PerformanceAggregatePoint[]>([]);
  const [aggregatesAAV, setAggregatesAAV] = createSignal<PerformanceAggregatePoint[]>([]);
  // Keep aggregates for backward compatibility (will be set to aggregatesAVG)
  const [aggregates, setAggregates] = createSignal<PerformanceAggregatePoint[]>([]);
  const [cloud, setCloud] = createSignal<PerformanceCloudPoint[]>([]);
  const [targets, setTargets] = createSignal<PerformanceTargetData>({ name: '', data: {} });
  const [showTargetModal, setShowTargetModal] = createSignal(false);
  const [availableTargets, setAvailableTargets] = createSignal<string[]>([]);
  const [selectedTargetName, setSelectedTargetName] = createSignal<string>('');
  const [targetColor, setTargetColor] = createSignal<string>(DEFAULT_PERFORMANCE_TARGET_COLOR);
  const [modalTargetColor, setModalTargetColor] = createSignal<string>(DEFAULT_PERFORMANCE_TARGET_COLOR);
  const [charts, setCharts] = createSignal<any[]>([]);
  
  // Trigger signal to force getFilteredAggregates to recalculate when filters are applied
  const [filterTrigger, setFilterTrigger] = createSignal(0);

  useAssistantPageScope(() => ({
    scope_kind: 'dataset',
    page_details: {
      races: selectedRacesAggregates(),
      legs: selectedLegsAggregates(),
      states: selectedStatesAggregates(),
      grades: selectedGradesAggregates(),
      filter_trigger: filterTrigger(),
    },
  }));

  // Local state for UI management only (no selection state)

  // Get color options from filter config (dataset context)
  const [colors, setColors] = createSignal<string[]>([]); // Start empty, will be populated from filter config
  onMount(() => {
    // 1. Set defaults immediately so UI can render
    setColors(finalizePerformanceColorOptions(['TACK', 'GRADE', 'STATE', 'SELECTIONS'])); // Fallback defaults
    setPersistentSettingsInitialized(true); // Mark as initialized so charts can render
    
    // 2. Load actual values in background (don't await)
    (async () => {
      try {
        // Explicitly call getFilterConfig to ensure caching happens (background)
        UnifiedFilterService.getFilterConfig(
          selectedClassName(),
          'dataset',
          selectedProjectId()
        ).catch(err => logDebug('Performance: Error loading filter config in background:', err));
        
        const colorOptions = await UnifiedFilterService.getColorOptions(
          selectedClassName(),
          'dataset',
          selectedProjectId()
        );
        
        if (colorOptions && colorOptions.length > 0) {
          // Add STATE to color options (it's a calculated field, not in filter config)
          const optionsWithState = [...colorOptions];
          if (!optionsWithState.includes('STATE')) {
            optionsWithState.push('STATE');
          }
          if (!optionsWithState.includes('SELECTIONS')) {
            optionsWithState.push('SELECTIONS');
          }
          setColors(finalizePerformanceColorOptions(optionsWithState));
          
          // Load color preference from persistent settings in background
          const currentUser = user();
          if (currentUser?.user_id) {
            try {
              const { selectedClassName, selectedProjectId } = persistantStore;
              const className = selectedClassName();
              const projectId = selectedProjectId();
              
              if (className && projectId) {
                const settings = await persistentSettingsService.loadSettings(
                  currentUser.user_id,
                  className,
                  projectId
                );
                
                if (settings?.performanceColor) {
                  const savedColor = cleanQuotes(String(settings.performanceColor));
                  // Only set if it's a valid color option (include client-appended options)
                  if (optionsWithState.includes(savedColor)) {
                    setColor(savedColor);
                    setColorLoadedFromPersistent(true); // Mark that we loaded color from persistent settings
                    logDebug('Performance: Loaded color preference from persistent settings', savedColor);
                  } else {
                    logDebug('Performance: Saved color preference not in available options, using default', savedColor);
                    // Set default color to first option if saved color is not available
                    if (color() === 'TACK' && colorOptions.length > 0) {
                      if (colorOptions.includes('TACK')) {
                        setColor('TACK');
                      } else {
                        setColor(colorOptions[0]);
                      }
                    }
                  }
                } else {
                  // No saved preference, set default color to first option if not already set
                  if (color() === 'TACK' && colorOptions.length > 0) {
                    // Only change if still on default TACK
                    if (colorOptions.includes('TACK')) {
                      setColor('TACK');
                    } else {
                      setColor(colorOptions[0]);
                    }
                  }
                }
              }
            } catch (error) {
              logDebug('Performance: Error loading color preference from persistent settings:', error);
              // Keep default color
            }
          }
        } else {
          // No color options found, keep defaults
          logDebug('Performance: No color options found, using defaults');
        }
      } catch (error) {
        logError('Error loading color options:', error);
        // Keep defaults on error
      }
    })();
  });
  const [groups, setGroups] = createSignal<{ name: string; color: string }[]>([
    { name: 'PORT', color: "#d62728" },
    { name: 'STBD', color: "#2ca02c" }
  ]);

  const [showUwDw, setShowUwDw] = createSignal(true);
  const [loading, setLoading] = createSignal(true);
  const [updateCharts, setUpdateCharts] = createSignal(true);
  const [mouseID, setMouseID] = createSignal<string | null>(null);

  const [color, setColor] = createSignal<string>("TACK");
  const [UwDw, setUwDw] = createSignal<string>("UPWIND");
  /** True while charts are re-rendering after upwind/downwind (or plot type) switch; used to show overlay. */
  const [modeSwitching, setModeSwitching] = createSignal<boolean>(false);

  createEffect(() => {
    setPerformanceSelectionsHotkeysActive(color() === 'SELECTIONS');
  });
  onCleanup(() => setPerformanceSelectionsHotkeysActive(false));
  
  // Track when persistent settings (including color) are initialized
  // This prevents charts from rendering before color preference is loaded
  const [persistentSettingsInitialized, setPersistentSettingsInitialized] = createSignal(false);
  
  // Track if color was loaded from persistent settings (so we can recompute groups after data loads)
  const [colorLoadedFromPersistent, setColorLoadedFromPersistent] = createSignal(false);
  
  // Plot type state (Scatter or Box)
  const [plotType, setPlotType] = createSignal<string>("Scatter");
  
  // TWS bin state for box plot mode
  const [twsBin, setTwsBin] = createSignal<string>("ALL");
  const [twsBinOptions, setTwsBinOptions] = createSignal<string[]>(["ALL"]);

  // Timeline visibility state
  const [showTimeline, setShowTimeline] = createSignal<boolean>(true);

  // Handler for timeline changes
  const handleTimelineChange = (value: boolean) => {
    setShowTimeline(value);
  };

  // Training/Racing filter state
  const [selectedTrainingRacing, setSelectedTrainingRacing] = createSignal<'TRAINING' | 'RACING' | null>(null);

  // Handle point of sail changes
  const handleUwDwChange = (value: string) => {
    // Only update if value actually changed
    if (UwDw() === value) {
      logDebug('Performance: UwDw value unchanged, skipping update', value);
      return;
    }
    logDebug('Performance: UwDw changing from', UwDw(), 'to', value);
    setModeSwitching(true);
    setUwDw(value);
    // Clear selection when switching between upwind and downwind (preserve filters)
    clearSelection({ preserveFilters: true, domain: 'aggregate' });
    setUpdateCharts(true);
  };
  
  // Handle plot type changes
  const handlePlotTypeChange = async (value: string) => {
    setPlotType(value);
    // Reset TWS bin to "ALL" when switching to Scatter mode
    if (value === "Scatter") {
      setTwsBin("ALL");
    }
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'Performance.tsx', 'Performance Report', `Plot type changed to ${value}`);
    setUpdateCharts(true);
  };
  
  // Handle TWS bin changes
  const handleTwsBinChange = async (value: string) => {
    setTwsBin(value);
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'Performance.tsx', 'Performance Report', `TWS bin changed to ${value}`);
    setUpdateCharts(true);
  };
  
  const [xAxis, setXAxis] = createSignal<string>(defaultChannelsStore.twsName());
  const [cloudType, setCloudType] = createSignal<string>("Latest");

  // Filter state - initialize from sessionStorage (grade/state set only from project or persistent in async block)
  const loadFiltersFromSession = (): void => {
    try {
      const savedFilters = sessionStorage.getItem('performanceFilters');
      if (savedFilters) {
        const filterData = JSON.parse(savedFilters);
        // Year/Event/Config/State are history-page-only filters; never read them here. The history
        // pages force a single state (H0 when empty) so their SQL always narrows — inheriting that
        // would silently hide every H1/H2 row on this page, which defaults to ALL. This page's own
        // state preference comes from persistent settings below.
        if (filterData.filterGrades != null && String(filterData.filterGrades).trim() !== '') setFilterGrades(cleanQuotes(String(filterData.filterGrades)));
        logDebug('Performance: Loaded filters from sessionStorage', filterData);
      }
    } catch (error) {
      logDebug('Performance: Error loading filters from sessionStorage:', error);
    }
  };

  // Load grade and state filters from persistent settings (overrides sessionStorage)
  const loadGradeStateFiltersFromPersistentSettings = async (): Promise<void> => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          const settings = await persistentSettingsService.loadSettings(
            currentUser.user_id,
            className,
            projectId
          );

          if (settings?.performanceFilters) {
            const filters = settings.performanceFilters;
            // Only load grade and state filters (persistent)
            if (filters.grades && typeof filters.grades === 'string') {
              setFilterGrades(cleanQuotes(filters.grades));
              logDebug('Performance: Loaded grade filter from persistent settings', cleanQuotes(filters.grades));
            }
            if (filters.state && typeof filters.state === 'string') {
              setFilterState(cleanQuotes(filters.state));
              logDebug('Performance: Loaded state filter from persistent settings', cleanQuotes(filters.state));
            }
            if (filters.trainingRacing === 'TRAINING' || filters.trainingRacing === 'RACING') {
              setSelectedTrainingRacing(filters.trainingRacing);
              logDebug('Performance: Loaded trainingRacing filter from persistent settings', filters.trainingRacing);
            }
          }
        }
      } catch (error) {
        logDebug('Performance: Error loading grade/state filters from persistent settings:', error);
      }
    }
  };

  // Save grade, state, and training/racing filters to persistent settings
  const saveGradeStateFiltersToPersistentSettings = async (): Promise<void> => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          await persistentSettingsService.saveSettings(
            currentUser.user_id,
            className,
            projectId,
            {
              performanceFilters: {
                grades: filterGrades(),
                state: filterState(),
                trainingRacing: selectedTrainingRacing()
              }
            }
          );
          logDebug('Performance: Saved grade, state, and trainingRacing filters to persistent settings', {
            grades: filterGrades(),
            state: filterState(),
            trainingRacing: selectedTrainingRacing()
          });
        }
      } catch (error) {
        logDebug('Performance: Error saving grade/state/trainingRacing filters to persistent settings:', error);
      }
    }
  };

  const [filterGrades, setFilterGrades] = createSignal<string>('');
  // Year/Event/Config are history-page-only filters — no setters: this page never applies them
  const [filterYear] = createSignal<string>('');
  const [filterEvent] = createSignal<string>('');
  const [filterConfig] = createSignal<string>('');
  const [filterCrew, setFilterCrew] = createSignal<string>('');
  const [filterState, setFilterState] = createSignal<string>('');

  // Every field here is applied in SQL, so a change must re-request — the old payload does not
  // contain the rows the new filter asks for. State belongs in this list: it used to be filtered
  // client-side at fetch time while sitting outside the signature, which permanently pinned
  // aggregates() to whatever state was active on load (H0 in practice) with no way back.
  const getPerformanceApiFiltersSignature = (): string =>
    JSON.stringify({
      grades: filterGrades(),
      year: filterYear(),
      event: filterEvent(),
      config: filterConfig(),
      crew: filterCrew(),
      state: filterState(),
    });

  const PERFORMANCE_PAGE_KEY = 'Performance';
  useDashboardShareRestore(
    PERFORMANCE_PAGE_KEY,
    () =>
      buildPerformanceShareState({
        plotType: plotType(),
        UwDw: UwDw(),
        showUwDw: showUwDw(),
        showTimeline: showTimeline(),
        color: color(),
        twsBin: twsBin(),
        selectedTargetName: selectedTargetName(),
        selectedTargetColor: targetColor(),
        selectedTrainingRacing: selectedTrainingRacing(),
        xAxis: xAxis(),
        cloudType: cloudType(),
        zoom: zoom(),
        filterGrades: filterGrades(),
        filterYear: filterYear(),
        filterEvent: filterEvent(),
        filterConfig: filterConfig(),
        filterState: filterState(),
        selectedRacesAggregates: [...selectedRacesAggregates()],
        selectedLegsAggregates: [...selectedLegsAggregates()],
        selectedGradesAggregates: [...selectedGradesAggregates()],
        selectedStatesAggregates: [...selectedStatesAggregates()],
        selectedEvents: [...selectedAggregateEvents()],
        hasSelection: hasSelection(),
        performanceSelection: selection(),
      }),
    (obj) =>
      applyPerformanceShareState(obj, PERFORMANCE_PAGE_KEY, {
        setPlotType,
        setUwDw,
        setShowUwDw,
        setShowTimeline,
        setColor,
        setTwsBin,
        setSelectedTargetName,
        setSelectedTargetColor: setTargetColor,
        setSelectedTrainingRacing,
        setXAxis,
        setCloudType,
        setZoom,
        setFilterGrades,
        // Year/Event/Config are history-page-only filters; ignore them in share payloads
        // (applyStringField calls the setter unguarded, so pass no-ops rather than omitting)
        setFilterYear: () => {},
        setFilterEvent: () => {},
        setFilterConfig: () => {},
        setFilterState,
      })
  );

  // Build filters object from filter values
  type PerformanceFilters = {
    GRADE?: number[];
    YEAR?: number[];
    EVENT?: string[];
    CONFIG?: string[];
    CREW?: string[];
    STATE?: string[];
    RACE?: (number | string)[];
    LEG?: number[];
  };

  const buildFilters = (): PerformanceFilters | undefined => {
    const filters: PerformanceFilters = {};

    // Parse grades (comma-separated, convert to integers)
    if (filterGrades().trim()) {
      const grades = filterGrades().split(',').map(g => parseInt(g.trim())).filter(g => !isNaN(g) && (g >= 0 && g <= 3));
      if (grades.length > 0) {
        filters.GRADE = grades;
      }
    }

    // Parse year (comma-separated, convert to integers)
    if (filterYear().trim()) {
      const years = filterYear().split(',').map(y => parseInt(y.trim())).filter(y => !isNaN(y));
      if (years.length > 0) {
        filters.YEAR = years;
      }
    }

    // Parse event (comma-separated strings)
    if (filterEvent().trim()) {
      const events = filterEvent().split(',').map(e => e.trim()).filter(e => e.length > 0);
      if (events.length > 0) {
        filters.EVENT = events;
      }
    }

    // Parse config (comma-separated strings)
    if (filterConfig().trim()) {
      const configs = filterConfig().split(',').map(c => c.trim()).filter(c => c.length > 0);
      if (configs.length > 0) {
        filters.CONFIG = configs;
      }
    }

    appendCrewCodeToFilters(filters, filterCrew());

    // Parse state (comma-separated strings). Empty or "ALL" = no state filter (show all states).
    const stateRaw = filterState().trim();
    if (stateRaw && stateRaw.toUpperCase() !== 'ALL') {
      const states = filterState().split(',').map(s => s.trim().toUpperCase()).filter(s => s === 'H0' || s === 'H1' || s === 'H2');
      if (states.length > 0) {
        filters.STATE = states;
      }
    }

    // Parse race numbers from selectedRaces (client-side filter)
    const races = selectedRacesAggregates();
    if (races && races.length > 0) {
      filters.RACE = races.map(r => {
        // Handle 'TRAINING' string or -1
        if (typeof r === 'number' && r === -1) {
          return 'TRAINING';
        }
        const rStr = String(r).toUpperCase();
        if (rStr === 'TRAINING' || rStr === '-1') {
          return 'TRAINING';
        }
        return typeof r === 'number' ? r : parseInt(String(r));
      }).filter(r => !isNaN(r as number) || r === 'TRAINING');
    }

    // Parse leg numbers from selectedLegs (client-side filter)
    const legs = selectedLegsAggregates();
    if (legs && legs.length > 0) {
      filters.LEG = legs.map(l => typeof l === 'number' ? l : parseInt(String(l))).filter(l => !isNaN(l));
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const [selectedChart, setSelectedChart] = createSignal<any[]>([]);
  const [zoom, setZoom] = createSignal(false);
  let scrollInnerEl: HTMLElement | undefined;
  const plotScrollRestore = createPerformanceChartsScrollRestore({
    getZoom: zoom,
    getScrollInner: () => scrollInnerEl
  });

  // Local loading and error states
  const [isLoading, setIsLoading] = createSignal(false);
  const [requestError, setRequestError] = createSignal<string | null>(null);

  // Data filtering is now handled automatically by the unified data store
  // based on global selection state - no manual cut data management needed

  let initializeChartsSeq = 0;
  let lastInitSignature = '';
  let filterEffectsEnabled = false;
  let lastFilters = '';
  // The foiling state / crew the current aggregates() payload was fetched under (SQL filters).
  let lastFetchedStateFilter = '';
  let lastFetchedCrewFilter = '';

  const getInitSignature = (preserveTargetName?: string): string =>
    JSON.stringify({
      datasetId: typeof selectedDatasetId === 'function' ? selectedDatasetId() : 0,
      className: selectedClassName(),
      projectId: selectedProjectId(),
      preserveTargetName: preserveTargetName ?? '',
    });

  const requestInitializeCharts = (preserveTargetName?: string, force = false): Promise<void> => {
    const sig = getInitSignature(preserveTargetName);
    if (!force && sig === lastInitSignature) {
      logDebug('Performance: Skipping duplicate initializeCharts', { sig });
      return Promise.resolve();
    }
    lastInitSignature = sig;
    // Record which SQL filters this payload was fetched under — Apply must refetch when they change.
    lastFetchedStateFilter = filterState();
    lastFetchedCrewFilter = filterCrew();
    return initializeCharts(preserveTargetName).then(() => undefined);
  };

  const initializeCharts = async (preserveTargetName?: string) => {
    const runSeq = ++initializeChartsSeq;
    setLoading(true);
    setIsLoading(true);
    setRequestError(null);

    // Clear any old data to prevent showing stale data from previous dataset/source
    setAggregates([]);
    setCloud([]);
    // Only clear targets if we're not preserving a specific target
    if (!preserveTargetName) {
      setTargets({ name: '', data: {} });
    }

    try {
      setUpdateCharts(false);

      // Get chart objects first to determine required channels
      const chartObjects = await performanceDataService.fetchCharts();
      if (runSeq !== initializeChartsSeq) return;
      setCharts(chartObjects);

      const channelMapping = performanceDataService.getChannelMapping(chartObjects);
      
      // Get aggregate type mapping to group channels by aggregate type
      const groupedChannels = performanceDataService.getAggregateTypeMapping(chartObjects);
      
      // Check if ANY series have AVG aggregate type (fetch cloud data if any are AVG)
      // Cloud data will be conditionally passed to each chart based on its aggregate type
      const hasAvgSeries = chartObjects.some(co => 
        co.charts?.[0]?.series?.some((s: any) => {
          const aggType = (s.yType || 'AVG').toUpperCase();
          return aggType === 'AVG';
        })
      );

      // Fetch all performance data using the performance data service
      // Map cloudType to the format expected by fetchCloud
      let cloudTypeForFetch = cloudType();
      if (cloudTypeForFetch === '1Hz Scatter') {
        cloudTypeForFetch = 'Latest';
      }

      const filters = buildFilters();

      // Fetch aggregates separately by type (AVG, STD, AAV)
      // First, collect channels needed for each aggregate type
      const channelsByType: { [key: string]: string[] } = { AVG: [], STD: [], AAV: [] };
      groupedChannels.forEach(group => {
        const type = group.aggregateType.toUpperCase();
        if (channelsByType[type]) {
          channelsByType[type].push(...group.channels);
        }
      });
      
      // Remove duplicates from each type's channel list
      Object.keys(channelsByType).forEach(type => {
        channelsByType[type] = [...new Set(channelsByType[type])];
      });

      // ============================================================================
      // CLOUD DATA FETCH - TEMPORARILY DISABLED (MAIN DATA LOAD)
      // ============================================================================
      // TO RESTORE CLOUD DATA FETCHING:
      // 1. Replace Promise.resolve([]) with the commented fetchCloud call below
      // 2. Also restore cloud fetching in the cloudType change effect (~line 645)
      // 3. Also restore cloud fetching in the filter change effect (~line 729)
      // 4. Ensure cloud rendering is enabled in AdvancedScatter.tsx
      // 5. Ensure cloud buttons are visible in PerfSettings.tsx
      //
      // NOTES:
      // - Cloud data is 1Hz point data (many points per event_id)
      // - Only fetched when cloudType !== 'None' AND hasAvgSeries is true
      // - Uses performanceDataService.fetchCloud() with channels, cloudType, and filters
      // - Cloud data is processed and passed to AdvancedScatter via props.cloud
      // ============================================================================
      // Fetch cloud data if cloudType is not 'None' and we have at least one AVG series
      // If preserveTargetName is provided, fetch that specific target; otherwise fetch latest
      const [aggregatesAVGData, aggregatesSTDData, aggregatesAAVData, maybeCloudData, targetsData] = await Promise.all([
        channelsByType.AVG.length > 0 ? performanceDataService.fetchAggregates(channelsByType.AVG, 'AVG', filters) : Promise.resolve([]),
        channelsByType.STD.length > 0 ? performanceDataService.fetchAggregates(channelsByType.STD, 'STD', filters) : Promise.resolve([]),
        channelsByType.AAV.length > 0 ? performanceDataService.fetchAggregates(channelsByType.AAV, 'AAV', filters) : Promise.resolve([]),
        // RESTORE THIS:
        // (cloudTypeForFetch === 'None' || !hasAvgSeries) 
        //   ? Promise.resolve([]) 
        //   : performanceDataService.fetchCloud(
        //       performanceDataService.getRequiredChannels(chartObjects), 
        //       cloudTypeForFetch, 
        //       filters
        //     ),
        Promise.resolve([]), // TEMPORARILY: Always return empty array for cloud data
        preserveTargetName ? fetchTargetData(preserveTargetName) : performanceDataService.fetchTargets()
      ]);
      if (runSeq !== initializeChartsSeq) return;

      // Process each aggregate type separately with channel mapping
      const processedAVG = aggregatesAVGData.length > 0 
        ? performanceDataService.processPerformanceData(aggregatesAVGData, maybeCloudData, { name: '', data: {} }, channelMapping)
        : { aggregates: [], cloud: [], targets: { name: '', data: {} } };
      const processedSTD = aggregatesSTDData.length > 0
        ? performanceDataService.processPerformanceData(aggregatesSTDData, [], { name: '', data: {} }, channelMapping)
        : { aggregates: [], cloud: [], targets: { name: '', data: {} } };
      const processedAAV = aggregatesAAVData.length > 0
        ? performanceDataService.processPerformanceData(aggregatesAAVData, [], { name: '', data: {} }, channelMapping)
        : { aggregates: [], cloud: [], targets: { name: '', data: {} } };

      // Set separate aggregate data
      setAggregatesAVG(processedAVG.aggregates);
      setAggregatesSTD(processedSTD.aggregates);
      setAggregatesAAV(processedAAV.aggregates);
      
      // Also set aggregates to AVG for backward compatibility
      setAggregates(processedAVG.aggregates);
      setCloud(processedAVG.cloud);
      
      // Debug: Log cloud vs aggregate data counts to verify cloud is 1Hz data
      logDebug('[Performance] Data counts after processing:', {
        aggregatesAVG: processedAVG.aggregates.length,
        aggregatesSTD: processedSTD.aggregates.length,
        aggregatesAAV: processedAAV.aggregates.length,
        cloud: processedAVG.cloud.length,
        cloudToAggregateRatio: processedAVG.aggregates.length > 0 ? (processedAVG.cloud.length / processedAVG.aggregates.length).toFixed(2) : 'N/A',
        sampleCloudPoint: processedAVG.cloud.length > 0 ? {
          event_id: processedAVG.cloud[0].event_id,
          Datetime: processedAVG.cloud[0].Datetime,
          hasTwa: processedAVG.cloud[0].Twa !== undefined,
          hasTws: processedAVG.cloud[0].Tws !== undefined
        } : null,
        sampleAggregatePoint: processedAVG.aggregates.length > 0 ? {
          event_id: processedAVG.aggregates[0].event_id,
          Datetime: processedAVG.aggregates[0].Datetime,
          hasTwa: processedAVG.aggregates[0].Twa !== undefined,
          hasTws: processedAVG.aggregates[0].Tws !== undefined
        } : null
      });
      // Only set targets if we're not preserving a specific target (it's already set)
      if (!preserveTargetName) {
        setTargets(targetsData);
      }
      
      // Extract TWS bins from aggregatesAVG data for box plot mode (intervals of 5)
      if (processedAVG.aggregates && processedAVG.aggregates.length > 0) {
        const twsField = defaultChannelsStore.twsName();
        const uniqueTwsBins = new Set<number>();
        processedAVG.aggregates.forEach((point: any) => {
          const twsValue = point[twsField];
          if (twsValue !== undefined && twsValue !== null && !isNaN(Number(twsValue))) {
            // Round to nearest 5 knots for binning (e.g., 5, 10, 15, 20, 25)
            const roundedBin = Math.round(Number(twsValue) / 5) * 5;
            uniqueTwsBins.add(roundedBin);
          }
        });
        const sortedBins = Array.from(uniqueTwsBins).sort((a, b) => a - b);
        const twsBinsList = ['ALL', ...sortedBins.map(String)];
        setTwsBinOptions(twsBinsList);
      } else {
        setTwsBinOptions(['ALL']);
      }
      
      // Set targets from processedAVG (targets are always AVG)
      if (!preserveTargetName) {
        setTargets(processedAVG.targets);
      }

      // Check if charts have filters to determine if Upwind/Downwind should be shown
      let result = true;
      chartObjects.forEach(chartObject => {
        if (chartObject.charts[0].filters.length > 0) {
          result = false;
        }
      });
      setShowUwDw(result);

      // Rebuild legend/groups for current color from freshly loaded aggregates (always force:
      // handleColorChange skips when value === color() and !force, which leaves stale/empty groups
      // e.g. if user already chose SELECTIONS while loading, or default TACK matches).
      handleColorChange(color(), true);
      // Reset the flag after using it
      if (colorLoadedFromPersistent()) {
        setColorLoadedFromPersistent(false);
      }

      // Defer chart mount so loading overlay clears and the main thread can process input first.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (runSeq === initializeChartsSeq) {
            setUpdateCharts(true);
          }
        });
      });
    } catch (err: unknown) {
      logError('Error initializing performance charts:', err as any);
      setRequestError(err instanceof Error ? err.message : String(err));
    } finally {
      if (runSeq === initializeChartsSeq) {
        setLoading(false);
        setIsLoading(false);
        lastFilters = getPerformanceApiFiltersSignature();
        filterEffectsEnabled = true;
      }
    }
  };

  // Watch for dataset-id changes and set filterStore dates accordingly
  // This handles the case where the component is reused when navigating between pages
  let lastDatasetId: number | null = null;
  createEffect(() => {
    const currentDatasetId: number = typeof selectedDatasetId === 'function' ? Number(selectedDatasetId()) : 0;

    if (currentDatasetId <= 0) {
      lastDatasetId = currentDatasetId;
      return;
    }

    if (lastDatasetId === currentDatasetId) {
      return;
    }

    const previousDatasetId = lastDatasetId;
    lastDatasetId = currentDatasetId;

    // Initial mount: onMount loads dates and calls requestInitializeCharts
    if (previousDatasetId === null) {
      logDebug(`[Performance] Initial dataset-id: ${currentDatasetId}`);
      return;
    }

    void (async () => {
      logDebug(`[Performance] Dataset-id changed from ${previousDatasetId} to ${currentDatasetId}, clearing old data and re-initializing...`);
      filterEffectsEnabled = false;

      try {
        const response = await getData(
          `${apiEndpoints.app.datasets}/info?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&dataset_id=${encodeURIComponent(currentDatasetId)}`
        );

        if (response.success && response.data?.date) {
          let dateStr = response.data.date;
          if (dateStr.length === 8 && !dateStr.includes('-')) {
            dateStr = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
          }

          logDebug(`[Performance] Setting filterStore dates to dataset date: ${dateStr}`);
          setStartDate(dateStr);
          setEndDate(dateStr);
        }
      } catch (err) {
        logError('[Performance] Error fetching dataset date:', err);
      }

      setAggregates([]);
      setCloud([]);
      setTargets({ name: '', data: {} });
      setUpdateCharts(false);
      await requestInitializeCharts(undefined, true);
    })();
  });

  // Re-fetch cloud data when cloudType changes (but not on initial mount)
  let isInitialMount = true;
  let cloudEffectCount = 0;
  let lastCloudType = '';
  createEffect(async () => {
    cloudEffectCount++;

    // Detect infinite loops
    if (cloudEffectCount > 10) {
      logError('🚨 INFINITE LOOP DETECTED in Performance cloudType effect!', cloudEffectCount);
      cloudEffectCount = 0;
      return;
    }

    const currentCloudType = cloudType();
    const currentCharts = charts();

    // Skip if cloudType hasn't actually changed
    if (currentCloudType === lastCloudType && !isInitialMount) {
      cloudEffectCount = 0;
      return;
    }
    lastCloudType = currentCloudType;

    // Skip on initial mount (initialization handles first load)
    if (isInitialMount) {
      isInitialMount = false;
      cloudEffectCount = 0;
      return;
    }

    // Only re-fetch if we have charts loaded and cloudType is set
    if (currentCharts.length > 0 && currentCloudType && !loading()) {
      const channelMapping = performanceDataService.getChannelMapping(currentCharts);

      // Check if ANY series have AVG aggregate type (fetch cloud data if any are AVG)
      const hasAvgSeries = currentCharts.some(co => 
        co.charts?.[0]?.series?.some((s: any) => {
          const aggType = (s.yType || 'AVG').toUpperCase();
          return aggType === 'AVG';
        })
      );

      // Map cloudType to the format expected by fetchCloud
      let cloudTypeForFetch = currentCloudType;
      if (cloudTypeForFetch === '1Hz Scatter') {
        cloudTypeForFetch = 'Latest';
      }

      try {
        setIsLoading(true);
        // Clear old cloud data immediately to prevent showing stale data
        setCloud([]);
        const filters = buildFilters();
        // ============================================================================
        // CLOUD DATA FETCH - TEMPORARILY DISABLED (CLOUD TYPE CHANGE EFFECT)
        // ============================================================================
        // TO RESTORE: Uncomment the fetchCloud call below and remove the empty array
        // This effect runs when cloudType changes (e.g., user selects different cloud option)
        // ============================================================================
        // RESTORE THIS:
        // const cloudData = (cloudTypeForFetch === 'None' || !hasAvgSeries) 
        //   ? [] 
        //   : await performanceDataService.fetchCloud(
        //       performanceDataService.getRequiredChannels(currentCharts), 
        //       cloudTypeForFetch, 
        //       filters
        //     );
        const cloudData: any[] = []; // TEMPORARILY: Always use empty array
        const processedData = performanceDataService.processPerformanceData(
          aggregates(),
          cloudData,
          targets(),
          channelMapping
        );
        setCloud(processedData.cloud);
        setUpdateCharts(true);
        cloudEffectCount = 0; // Reset on success
      } catch (err) {
        logError('Error fetching cloud data when cloudType changed:', err);
        cloudEffectCount = 0; // Reset on error
      } finally {
        setIsLoading(false);
      }
    } else {
      cloudEffectCount = 0; // Reset if conditions not met
    }
  });

  // Watch for filter changes and refetch cloud data when API-level filters change
  // Note: STATE, RACE, and LEG filters are client-side only, so they don't trigger cloud refetch
  // Only API filters (YEAR, EVENT, CONFIG, SOURCE_NAME, optionally GRADE) trigger refetch
  let filterEffectCount = 0;
  createEffect(async () => {
    if (!filterEffectsEnabled) {
      return;
    }

    filterEffectCount++;

    // Detect infinite loops
    if (filterEffectCount > 10) {
      logError('🚨 INFINITE LOOP DETECTED in Performance filter effect!', filterEffectCount);
      filterEffectCount = 0;
      return;
    }

    const currentFilters = getPerformanceApiFiltersSignature();

    // Skip if filters haven't actually changed
    if (currentFilters === lastFilters) {
      filterEffectCount = 0;
      return;
    }
    lastFilters = currentFilters;

    // Only refetch cloud data if we have charts loaded, cloudType is set, and not loading
    const currentCharts = charts();
    const currentCloudType = cloudType();
    if (currentCharts.length > 0 && currentCloudType && currentCloudType !== 'None' && !loading()) {
      const channelMapping = performanceDataService.getChannelMapping(currentCharts);

      // Check if ANY series have AVG aggregate type (fetch cloud data if any are AVG)
      const hasAvgSeries = currentCharts.some(co => 
        co.charts?.[0]?.series?.some((s: any) => {
          const aggType = (s.yType || 'AVG').toUpperCase();
          return aggType === 'AVG';
        })
      );

      // Map cloudType to the format expected by fetchCloud
      let cloudTypeForFetch = currentCloudType;
      if (cloudTypeForFetch === '1Hz Scatter') {
        cloudTypeForFetch = 'Latest';
      }

      try {
        setIsLoading(true);
        const filters = buildFilters();
        // ============================================================================
        // CLOUD DATA FETCH - TEMPORARILY DISABLED (FILTER CHANGE EFFECT)
        // ============================================================================
        // TO RESTORE: Uncomment the fetchCloud call below and remove the empty array
        // This effect runs when API-level filters change (YEAR, EVENT, CONFIG, SOURCE_NAME)
        // Note: Client-side filters (STATE, RACE, LEG) don't trigger cloud refetch
        // ============================================================================
        // RESTORE THIS:
        // const cloudData = !hasAvgSeries 
        //   ? [] 
        //   : await performanceDataService.fetchCloud(
        //       performanceDataService.getRequiredChannels(currentCharts), 
        //       cloudTypeForFetch, 
        //       filters
        //     );
        const cloudData: any[] = []; // TEMPORARILY: Always use empty array
        const processedData = performanceDataService.processPerformanceData(
          aggregates(),
          cloudData,
          targets(),
          channelMapping
        );
        setCloud(processedData.cloud);
        setUpdateCharts(true);
        filterEffectCount = 0; // Reset on success
      } catch (err) {
        logError('Error refetching cloud data when filters changed:', err);
        filterEffectCount = 0; // Reset on error
      } finally {
        setIsLoading(false);
      }
    } else {
      filterEffectCount = 0; // Reset if conditions not met
    }
  });

  // Populate TWS bins from existing aggregates when switching to Box mode
  createEffect(() => {
    const currentPlotType = plotType();
    const currentAggregates = aggregates();
    
    // Only populate TWS bins for Box mode
    if (currentPlotType === "Box" && currentAggregates.length > 0) {
      const twsField = defaultChannelsStore.twsName();
      const uniqueTwsBins = new Set<number>();
      currentAggregates.forEach((point: any) => {
        const twsValue = point[twsField];
        if (twsValue !== undefined && twsValue !== null && !isNaN(Number(twsValue))) {
          // Round to nearest 5 knots for binning (e.g., 5, 10, 15, 20, 25)
          const roundedBin = Math.round(Number(twsValue) / 5) * 5;
          uniqueTwsBins.add(roundedBin);
        }
      });
      const sortedBins = Array.from(uniqueTwsBins).sort((a, b) => a - b);
      const twsBinsList = ['ALL', ...sortedBins.map(String)];
      setTwsBinOptions(twsBinsList);
    }
  });

  const handleZoom = (info: any[]) => {
    if (info.length > 0) {
      plotScrollRestore.captureBeforeEnteringPlotZoom();
      setSelectedChart(info);
      setZoom(true);
    } else {
      setSelectedChart([]);
      setZoom(false);
    }
  };

  const handleAxisChange = async (axis: string) => {
    // axis is already the correct channel name from defaultChannelsStore (via PerfSettings)
    setXAxis(axis);
    setZoom(false);
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'Performance.tsx', 'Performance Report', `Axis changed to ${axis}`);
    // Force chart redraw by toggling updateCharts signal
    // This ensures charts react to x-axis changes even if updateCharts was already true
    setUpdateCharts(false);
    // Use setTimeout to ensure the false state is processed before setting to true
    setTimeout(() => setUpdateCharts(true), 0);
  }

  // Create chart layout based on current x-axis selection
  const chartGroups = createMemo(() => {
    const chartsData = charts() as any[];
    if (!chartsData || chartsData.length === 0) return [];

    const { twsName, bspName } = defaultChannelsStore;
    const currentXAxis = xAxis();
    const twsField = twsName();
    const bspField = bspName();

    // Simply ensure dataField is set for all series (use name if dataField doesn't exist)
    // Extract xType and yType for each series
    return chartsData.map((group: any) => {
      const series = group.charts[0].series.map((s: any) => {
        let yaxisDataField = s.yaxis?.dataField || s.yaxis?.name || '';
        const yaxisName = s.yaxis?.name || '';
        
        // Determine x-axis: use series xaxis if not PAGE DEFAULT, otherwise use global xAxis
        const seriesXAxis = s.xaxis?.name;
        const effectiveXAxis = (seriesXAxis && seriesXAxis !== "PAGE DEFAULT" && seriesXAxis !== "DEFAULT") ? seriesXAxis : currentXAxis;
        
        // Get aggregate types (xType and yType), default to AVG for backward compatibility
        const xType = (s.xType || 'AVG').toUpperCase();
        const yType = (s.yType || 'AVG').toUpperCase();
        
        // If Bsp is the y-axis and x-axis changes to Bsp, put TWS on the y-axis
        const isYAxisBsp = yaxisDataField.toLowerCase() === bspField.toLowerCase() || 
                          yaxisName.toLowerCase() === bspField.toLowerCase();
        const isXAxisBsp = effectiveXAxis.toLowerCase() === bspField.toLowerCase();
        
        if (isYAxisBsp && isXAxisBsp) {
          yaxisDataField = twsField;
        }
        
        return {
          ...s,
          xaxisValue: effectiveXAxis, // Store the effective x-axis value for this series
          xType: xType, // Store xType for data selection
          yType: yType, // Store yType for data selection
          yaxis: {
            ...s.yaxis,
            dataField: yaxisDataField
          },
          yaxisTarget: s.yaxisTarget ? {
            ...s.yaxisTarget,
            dataField: s.yaxisTarget?.dataField || s.yaxisTarget?.name || ''
          } : s.yaxisTarget
        };
      });
      
      return {
        ...group,
        charts: [{
          ...group.charts[0],
          series
        }]
      };
    });
  });

  /**
   * Baseline overlays: which saved baselines (and configs) are drawn over this dataset's scatter.
   *
   * Display only — baselines are saved from the Performance History pages, where the selection
   * spans the whole project rather than a single dataset. Two independent slots, so two baselines
   * can be compared against each other and the target at once.
   */
  const baselineSlot1 = useBaselineDisplay({
    getClassName: () => persistantStore.selectedClassName(),
    getProjectId: () => persistantStore.selectedProjectId(),
    slot: 1,
    onApplied: () => forcePerformanceChartUpdate(setUpdateCharts),
    onDeleted: (id) => clearDeletedBaselineFromSlots(id),
  });

  const baselineSlot2 = useBaselineDisplay({
    getClassName: () => persistantStore.selectedClassName(),
    getProjectId: () => persistantStore.selectedProjectId(),
    slot: 2,
    onApplied: () => forcePerformanceChartUpdate(setUpdateCharts),
    onDeleted: (id) => clearDeletedBaselineFromSlots(id),
  });

  const baselineSlots = [baselineSlot1, baselineSlot2];

  /** A delete removes the baseline for the whole project, so no slot may keep drawing it. */
  const clearDeletedBaselineFromSlots = (id: number): void => {
    for (const slot of baselineSlots) { slot.clearIfShowing(id); }
  };

  /** Overlays for the charts: the slots that actually have a baseline loaded, in slot order. */
  const baselineOverlays = (): BaselineOverlay[] => baselineSlots
    .filter((slot) => slot.baselineFit())
    .map((slot) => ({
      fit: slot.baselineFit(),
      config: slot.baselineConfig(),
      name: slot.baselineName(),
      color: slot.baselineColor(),
      // Part of the chart's redraw signature: without it, swapping to a different baseline that
      // happens to share a name and colour would not repaint.
      revision: slot.overlayRevision(),
    }));

  /** "Download Excel" in each picker: this report's channels, on this report's x-axis. */
  const baselineExport = useBaselineExcelExport({
    getXAxis: xAxis,
    getChartGroups: chartGroups,
  });
  const isExportingSlot = createSelector(() => baselineExport.exportingSlot());

  /**
   * Legend entries: always one per slot, filled or not, so both pickers stay reachable. While a
   * picker is open the swatch previews the colour being chosen.
   */
  const baselineLegendEntries = (): BaselineLegendEntry[] => baselineSlots.map((slot) => ({
    name: slot.baselineLabel(),
    color: slot.showModal() ? slot.modalColor() : slot.baselineColor(),
    onClick: () => void slot.openModal(),
  }));

  const handleColorChange = (value: string, force: boolean = false) => {
    if (!force && value === color()) return;

    // Compute new groups synchronously so chart has correct groups when it re-renders with new color (avoids white/lightgrey flash)
    const dataForGrouping = aggregates();
    const getFieldName = (colorField: string): string => {
      const fieldMap: Record<string, string> = {
        'TACK': 'tack', 'GRADE': 'grade', 'RACE': 'race_number', 'LEG': 'leg_number',
        'CONFIG': 'config', 'CREW': 'crew', 'TESTS': 'tests', 'YEAR': 'year', 'EVENT': 'event', 'SOURCE_NAME': 'source_name', 'STATE': 'state',
        'SELECTIONS': 'selection'
      };
      return fieldMap[colorField] || colorField;
    };
    // Build unique values using same resolution as chart (resolveDataField) so group names match getColor lookup exactly
    // For STATE, use same fallback chain as AdvancedScatter so dataset page works like PerformanceHistory
    const getPointValue = (d: any): any => {
      let v: any;
      if (value === 'STATE') {
        v = d.state ?? d.State ?? d.STATE ?? resolveDataField(d, 'STATE');
      } else {
        v = resolveDataField(d, value);
      }
      if (value === 'RACE' && (v === -1 || v === '-1')) return 'TRAINING';
      if (value === 'TESTS' && (v === undefined || v === null || v === '')) return 'NONE';
      return v;
    };
    let unique_vals: any[] =
      value === 'TACK'
        ? groupBy(dataForGrouping, getFieldName(value))
        : value === 'SELECTIONS'
          ? (() => {
              const set = new Set<number>();
              for (const d of dataForGrouping) {
                const raw = d.selection ?? d.SELECTIONS ?? resolveDataField(d, 'SELECTIONS');
                const n = Number(raw);
                set.add(Number.isFinite(n) ? n : 0);
              }
              return [...set];
            })()
        : [...new Set(dataForGrouping.map(getPointValue))].filter((v) => v !== undefined && v !== null);

    if (value === 'STATE') {
      logDebug('Performance: handleColorChange - State field:', {
        colorField: value,
        dataField: getFieldName(value),
        dataLength: dataForGrouping.length,
        uniqueVals: unique_vals,
        uniqueValsLength: unique_vals.length,
        sampleStates: dataForGrouping.slice(0, 5).map(d => d.state)
      });
    }

    let newGroups: { name: string; color: string }[];
    if (isPerformanceRampColor(value)) {
      // DENSITY / VMG are per-chart ramps drawn by AdvancedScatter, not a set of values: no groups.
      newGroups = [];
    } else if (value == 'TACK') {
      newGroups = [{ name: 'PORT', color: "#d62728" }, { name: 'STBD', color: "#2ca02c" }];
    } else if (value == 'GRADE') {
      newGroups = unique_vals.map((val: any) => {
        const gradeNum = typeof val === 'number' ? val : parseInt(val, 10);
        if (isNaN(gradeNum) || gradeNum === 0 || val == 'NONE' || val === null || val === undefined) {
          return { name: val === null || val === undefined ? 'null' : val, color: 'lightgrey' };
        }
        if (gradeNum === 1) return { name: val, color: 'red' };
        if (gradeNum === 2) return { name: val, color: 'green' };
        if (gradeNum === 3) return { name: val, color: 'yellow' };
        return { name: val, color: getColorByIndex(gradeNum - 1) };
      });
    } else {
      const numericFields = ['LEG', 'RACE'];
      const isNumericField = numericFields.includes(value);
      if (value === 'RACE') {
        unique_vals = unique_vals.sort((a: any, b: any) => {
          const aIsTraining = a === 'TRAINING' || a === -1 || a === '-1';
          const bIsTraining = b === 'TRAINING' || b === -1 || b === '-1';
          if (aIsTraining && !bIsTraining) return -1;
          if (!aIsTraining && bIsTraining) return 1;
          const aNum = aIsTraining ? -1 : (typeof a === 'number' ? a : Number(a));
          const bNum = bIsTraining ? -1 : (typeof b === 'number' ? b : Number(b));
          return aNum - bNum;
        });
      }
      if (value === 'SELECTIONS') {
        unique_vals = unique_vals.sort((a: any, b: any) => Number(a) - Number(b));
      }
      newGroups = unique_vals.map((val: any, index: number) => {
        if (value === 'RACE' && (val === 'TRAINING' || val === -1 || val === '-1')) {
          return { name: 'TRAINING', color: 'lightgrey' };
        }
        if (val == 'NONE' || val == null || val === '') {
          return { name: val === null || val === undefined ? 'null' : val, color: 'lightgrey' };
        }
        if (!isNumericField && val == 0) return { name: val, color: 'lightgrey' };
        return { name: val, color: getColorByIndex(index) };
      });
    }

    batch(() => {
      setGroups(newGroups);
      setColor(value);
    });

    requestAnimationFrame(() => {
      setUpdateCharts(true);
      saveColorPreference(value);
    });
  }

  const onLocalPerformanceSelectionAssign = (eventIds: number[], value: number) => {
    assignLocalPerformanceSelection(
      eventIds,
      value,
      [
        { get: aggregates, set: setAggregates },
        { get: aggregatesAVG, set: setAggregatesAVG },
        { get: aggregatesSTD, set: setAggregatesSTD },
        { get: aggregatesAAV, set: setAggregatesAAV }
      ],
      [{ get: cloud, set: setCloud }]
    );
    requestAnimationFrame(() => {
      setUpdateCharts(true);
      handleColorChange(color(), true);
    });
  };
  
  // Save color preference to persistent settings (debounced)
  let colorSaveTimeout: NodeJS.Timeout | null = null;
  const saveColorPreference = async (colorValue: string) => {
    // Clear existing timeout
    if (colorSaveTimeout) {
      clearTimeout(colorSaveTimeout);
    }
    
    // Debounce saves by 500ms
    colorSaveTimeout = setTimeout(async () => {
      const currentUser = user();
      if (currentUser?.user_id) {
        try {
          const { selectedClassName, selectedProjectId } = persistantStore;
          const className = selectedClassName();
          const projectId = selectedProjectId();
          
          if (className && projectId) {
            await persistentSettingsService.saveSettings(
              currentUser.user_id,
              className,
              projectId,
              { performanceColor: colorValue }
            );
            logDebug('Performance: Saved color preference to persistent settings', colorValue);
          }
        } catch (error) {
          logDebug('Performance: Error saving color preference to persistent settings:', error);
        }
      }
      colorSaveTimeout = null;
    }, 500);
  };

  const exploreMapTimeWindowContext = (): MapScopeContext => ({
    exploreMapMountedCount: exploreMapMountedCount(),
    isInSplitView: props.isInSplitView,
    mainPanelMenu: props.mainPanelMenu,
    rightPanelMenu: props.rightPanelMenu,
  });

  const exploreMapActive = createMemo(() =>
    isExploreMapActiveForReports(exploreMapTimeWindowContext())
  );

  const exploreActiveTimeWindow = createMemo(() => {
    const ref = getDisplayWindowReferenceTime();
    const quantizedRef = isPlaying()
      ? quantizeFleetReferenceTime(ref, 1000)
      : ref;
    return computeExploreMapReportActiveTimeWindow(
      exploreMapTimeWindowContext(),
      timeWindow(),
      quantizedRef
    );
  });

  const applyExploreMapTimeScope = <T extends Record<string, unknown>>(rows: T[]): T[] =>
    applyMapTimeScopeIfActive(rows, exploreMapActive(), exploreActiveTimeWindow());

  const exploreWindowScopeKey = createMemo(() =>
    fleetActiveTimeWindowScopeKey(exploreActiveTimeWindow(), 1000)
  );

  const exploreReportScopeSig = createMemo(() =>
    mapReportScopeSignature(exploreMapActive(), exploreWindowScopeKey())
  );

  // When explore map is active, mirror map race/leg filters into performance aggregates context.
  let lastExploreMapRaceLegSig = "";
  createEffect(() => {
    if (!exploreMapActive()) return;
    void selectedRacesTimeseries();
    void selectedLegsTimeseries();
    const sig = fleetMapRaceLegFilterSignature();
    if (sig === lastExploreMapRaceLegSig) return;
    lastExploreMapRaceLegSig = sig;
    const { races, legs } = readFleetMapRaceLegFilters();
    setSelectedRacesAggregates(fleetReportRaceSelectionsFromMap(races, isTrainingHourMode()));
    setSelectedLegsAggregates(legs.map((l) => String(l)));
    setUpdateCharts(true);
  });

  // Re-draw charts when explore map opens/closes in split or playback window moves.
  let lastExplorePerfScopeSig = "";
  createEffect(() => {
    const sig = exploreReportScopeSig();
    if (sig === lastExplorePerfScopeSig) return;
    lastExplorePerfScopeSig = sig;
    setUpdateCharts(true);
  });

  // Helper to get filtered data (memoized for efficiency to prevent repeated filtering with many charts)
  // Helper function to filter aggregates by type
  // Reactive memos for filtered aggregates by type - these automatically update when cutEvents changes
  const getFilteredAggregatesAVG = createMemo(() => {
    const data = aggregatesAVG();
    if (!Array.isArray(data)) {
      return [];
    }
    
    // Access filterTrigger to ensure recalculation when filters are applied
    filterTrigger();
    let filteredData = [...data];

    // Apply selection filtering if there are cut events - ACCESS cutEvents() to ensure reactivity
    const currentCuts = cutEvents();
    if (currentCuts.length > 0) {
      filteredData = applyCutToRows(filteredData, currentCuts);
    }

    // Apply client-side filters (use filterStore like FleetPerformance / FleetPerformanceHistory)
    const races = selectedRacesAggregates();
    const legs = selectedLegsAggregates();
    const grades: number[] = [];
    const gradeValues = selectedGradesAggregates();
    if (Array.isArray(gradeValues) && gradeValues.length > 0) {
      grades.push(...gradeValues.map((g: string | number) => (typeof g === 'number' ? g : Number(g))).filter((n: number) => !isNaN(n) && (n >= 0 && n <= 3)));
    }
    const states: string[] = [];
    const stateValues = selectedStatesAggregates();
    if (Array.isArray(stateValues) && stateValues.length > 0) {
      states.push(...stateValues.map((s: string) => String(s)));
    }
    
    if (races.length > 0 || legs.length > 0 || grades.length > 0 || states.length > 0) {
      const filterConfig = createFilterConfig([], races, legs, grades);
      const fullFilterConfig = {
        ...filterConfig,
        twaStates: [],
        states: states,
      };
      filteredData = filteredData.filter((point: any) => passesBasicFilters(point, fullFilterConfig));
    }

    // Apply Training/Racing filter
    const trainingRacing = selectedTrainingRacing();
    if (trainingRacing) {
      filteredData = filteredData.filter((point: any) => passesManeuverTrainingRacingFilter(point, trainingRacing));
    }

    // Apply TWS bin filtering
    const currentTwsBin = twsBin();
    if (currentTwsBin && currentTwsBin !== 'ALL') {
      const twsField = defaultChannelsStore.twsName();
      const targetBin = Number(currentTwsBin);
      if (!isNaN(targetBin)) {
        filteredData = filteredData.filter((d: any) => {
          const twsValue = d[twsField];
          if (twsValue !== undefined && twsValue !== null && !isNaN(Number(twsValue))) {
            const roundedTws = Math.round(Number(twsValue) / 5) * 5;
            return roundedTws === targetBin;
          }
          return false;
        });
      }
    }

    // Apply point of sail filtering
    // Use Twa_deg_avg (metadata from AVG) for consistent upwind/downwind filtering across all aggregate types
    if (showUwDw()) {
      if (UwDw() === 'UPWIND') {
        filteredData = filteredData.filter((d: PerformanceAggregatePoint) => {
          const twaAvg = (d as any).Twa_deg_avg ?? (d as any).twa_deg_avg;
          if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Math.abs(Number(twaAvg)) < 90;
          }
          // Fallback to regular Twa_deg if Twa_deg_avg is missing
          const twaField = defaultChannelsStore.twaName();
          const twaValue = (d as any)[twaField];
          return twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue)) && Math.abs(Number(twaValue)) < 90;
        });
      } else {
        filteredData = filteredData.filter((d: PerformanceAggregatePoint) => {
          const twaAvg = (d as any).Twa_deg_avg ?? (d as any).twa_deg_avg;
          if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Math.abs(Number(twaAvg)) >= 90;
          }
          // Fallback to regular Twa_deg if Twa_deg_avg is missing
          const twaField = defaultChannelsStore.twaName();
          const twaValue = (d as any)[twaField];
          return twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue)) && Math.abs(Number(twaValue)) >= 90;
        });
      }
    }

    return applyExploreMapTimeScope(filteredData);
  });

  const getFilteredAggregatesSTD = createMemo(() => {
    const data = aggregatesSTD();
    if (!Array.isArray(data)) {
      return [];
    }
    
    // Access filterTrigger to ensure recalculation when filters are applied
    filterTrigger();
    let filteredData = [...data];

    // Apply selection filtering if there are cut events - ACCESS cutEvents() to ensure reactivity
    const currentCuts = cutEvents();
    if (currentCuts.length > 0) {
      filteredData = applyCutToRows(filteredData, currentCuts);
    }

    // Apply client-side filters (use filterStore like FleetPerformance / FleetPerformanceHistory)
    const races = selectedRacesAggregates();
    const legs = selectedLegsAggregates();
    const grades: number[] = [];
    const gradeValues = selectedGradesAggregates();
    if (Array.isArray(gradeValues) && gradeValues.length > 0) {
      grades.push(...gradeValues.map((g: string | number) => (typeof g === 'number' ? g : Number(g))).filter((n: number) => !isNaN(n) && (n >= 0 && n <= 3)));
    }
    const states: string[] = [];
    const stateValues = selectedStatesAggregates();
    if (Array.isArray(stateValues) && stateValues.length > 0) {
      states.push(...stateValues.map((s: string) => String(s)));
    }
    
    if (races.length > 0 || legs.length > 0 || grades.length > 0 || states.length > 0) {
      const filterConfig = createFilterConfig([], races, legs, grades);
      const fullFilterConfig = {
        ...filterConfig,
        twaStates: [],
        states: states,
      };
      filteredData = filteredData.filter((point: any) => passesBasicFilters(point, fullFilterConfig));
    }

    // Apply Training/Racing filter
    const trainingRacing = selectedTrainingRacing();
    if (trainingRacing) {
      filteredData = filteredData.filter((point: any) => passesManeuverTrainingRacingFilter(point, trainingRacing));
    }

    // Apply TWS bin filtering
    const currentTwsBin = twsBin();
    if (currentTwsBin && currentTwsBin !== 'ALL') {
      const twsField = defaultChannelsStore.twsName();
      const targetBin = Number(currentTwsBin);
      if (!isNaN(targetBin)) {
        filteredData = filteredData.filter((d: any) => {
          const twsValue = d[twsField];
          if (twsValue !== undefined && twsValue !== null && !isNaN(Number(twsValue))) {
            const roundedTws = Math.round(Number(twsValue) / 5) * 5;
            return roundedTws === targetBin;
          }
          return false;
        });
      }
    }

    // Apply point of sail filtering
    // Use Twa_deg_avg (metadata from AVG) for consistent upwind/downwind filtering across all aggregate types
    if (showUwDw()) {
      if (UwDw() === 'UPWIND') {
        filteredData = filteredData.filter((d: PerformanceAggregatePoint) => {
          const twaAvg = (d as any).Twa_deg_avg ?? (d as any).twa_deg_avg;
          if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Math.abs(Number(twaAvg)) < 90;
          }
          // Fallback to regular Twa_deg if Twa_deg_avg is missing
          const twaField = defaultChannelsStore.twaName();
          const twaValue = (d as any)[twaField];
          return twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue)) && Math.abs(Number(twaValue)) < 90;
        });
      } else {
        filteredData = filteredData.filter((d: PerformanceAggregatePoint) => {
          const twaAvg = (d as any).Twa_deg_avg ?? (d as any).twa_deg_avg;
          if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Math.abs(Number(twaAvg)) >= 90;
          }
          // Fallback to regular Twa_deg if Twa_deg_avg is missing
          const twaField = defaultChannelsStore.twaName();
          const twaValue = (d as any)[twaField];
          return twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue)) && Math.abs(Number(twaValue)) >= 90;
        });
      }
    }

    return applyExploreMapTimeScope(filteredData);
  });

  const getFilteredAggregatesAAV = createMemo(() => {
    const data = aggregatesAAV();
    if (!Array.isArray(data)) {
      return [];
    }
    
    // Access filterTrigger to ensure recalculation when filters are applied
    filterTrigger();
    let filteredData = [...data];

    // Apply selection filtering if there are cut events - ACCESS cutEvents() to ensure reactivity
    const currentCuts = cutEvents();
    if (currentCuts.length > 0) {
      filteredData = applyCutToRows(filteredData, currentCuts);
    }

    // Apply client-side filters (use filterStore like FleetPerformance / FleetPerformanceHistory)
    const races = selectedRacesAggregates();
    const legs = selectedLegsAggregates();
    const grades: number[] = [];
    const gradeValues = selectedGradesAggregates();
    if (Array.isArray(gradeValues) && gradeValues.length > 0) {
      grades.push(...gradeValues.map((g: string | number) => (typeof g === 'number' ? g : Number(g))).filter((n: number) => !isNaN(n) && (n >= 0 && n <= 3)));
    }
    const states: string[] = [];
    const stateValues = selectedStatesAggregates();
    if (Array.isArray(stateValues) && stateValues.length > 0) {
      states.push(...stateValues.map((s: string) => String(s)));
    }
    
    if (races.length > 0 || legs.length > 0 || grades.length > 0 || states.length > 0) {
      const filterConfig = createFilterConfig([], races, legs, grades);
      const fullFilterConfig = {
        ...filterConfig,
        twaStates: [],
        states: states,
      };
      filteredData = filteredData.filter((point: any) => passesBasicFilters(point, fullFilterConfig));
    }

    // Apply Training/Racing filter
    const trainingRacing = selectedTrainingRacing();
    if (trainingRacing) {
      filteredData = filteredData.filter((point: any) => passesManeuverTrainingRacingFilter(point, trainingRacing));
    }

    // Apply TWS bin filtering
    const currentTwsBin = twsBin();
    if (currentTwsBin && currentTwsBin !== 'ALL') {
      const twsField = defaultChannelsStore.twsName();
      const targetBin = Number(currentTwsBin);
      if (!isNaN(targetBin)) {
        filteredData = filteredData.filter((d: any) => {
          const twsValue = d[twsField];
          if (twsValue !== undefined && twsValue !== null && !isNaN(Number(twsValue))) {
            const roundedTws = Math.round(Number(twsValue) / 5) * 5;
            return roundedTws === targetBin;
          }
          return false;
        });
      }
    }

    // Apply point of sail filtering
    // Use Twa_deg_avg (metadata from AVG) for consistent upwind/downwind filtering across all aggregate types
    if (showUwDw()) {
      if (UwDw() === 'UPWIND') {
        filteredData = filteredData.filter((d: PerformanceAggregatePoint) => {
          const twaAvg = (d as any).Twa_deg_avg ?? (d as any).twa_deg_avg;
          if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Math.abs(Number(twaAvg)) < 90;
          }
          // Fallback to regular Twa_deg if Twa_deg_avg is missing
          const twaField = defaultChannelsStore.twaName();
          const twaValue = (d as any)[twaField];
          return twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue)) && Math.abs(Number(twaValue)) < 90;
        });
      } else {
        filteredData = filteredData.filter((d: PerformanceAggregatePoint) => {
          const twaAvg = (d as any).Twa_deg_avg ?? (d as any).twa_deg_avg;
          if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
            return Math.abs(Number(twaAvg)) >= 90;
          }
          // Fallback to regular Twa_deg if Twa_deg_avg is missing
          const twaField = defaultChannelsStore.twaName();
          const twaValue = (d as any)[twaField];
          return twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue)) && Math.abs(Number(twaValue)) >= 90;
        });
      }
    }

    return applyExploreMapTimeScope(filteredData);
  });

  const getFilteredAggregates = createMemo(() => {
    // Access filterTrigger to ensure memo recalculates when filters are applied
    filterTrigger(); // Track dependency
    let data = aggregates();

    // Ensure data is always an array
    if (!Array.isArray(data)) {
      logWarn('[Performance] aggregates() is not an array, defaulting to empty array');
      return [];
    }

    // Cut events narrow the working set (distinct from brush selection; scatter highlights selection on full filtered set)
    const currentCuts = cutEvents();

    if (currentCuts.length > 0) {
      data = applyCutToRows(data, currentCuts);
    }

    // Apply client-side filters: STATE, RACE, LEG, GRADE (use filterStore like FleetPerformance / FleetPerformanceHistory)
    const races = selectedRacesAggregates();
    const legs = selectedLegsAggregates();
    const grades: number[] = [];
    const gradeValues = selectedGradesAggregates();
    if (Array.isArray(gradeValues) && gradeValues.length > 0) {
      grades.push(...gradeValues.map((g: string | number) => (typeof g === 'number' ? g : Number(g))).filter((n: number) => !isNaN(n) && (n >= 0 && n <= 3)));
    }
    const states: string[] = [];
    const stateValues = selectedStatesAggregates();
    if (Array.isArray(stateValues) && stateValues.length > 0) {
      states.push(...stateValues.map((s: string) => String(s)));
    }
    
    // Apply client-side filters if any are active
    if (races.length > 0 || legs.length > 0 || grades.length > 0 || states.length > 0) {
      const filterConfig = createFilterConfig(
        [], // twaStates
        races,
        legs,
        grades
      );
      
      // Add STATE filter to config (separate from twaStates)
      const fullFilterConfig = {
        ...filterConfig,
        twaStates: [],
        states: states, // State field filter (e.g., H0, H1, H2) - always client-side
      };
      
      data = data.filter((point: any) => {
        // Data is normalized to lowercase fields (race_number, leg_number, grade, state) by processPerformanceData
        // passesBasicFilters expects normalized lowercase fields and handles type conversion internally
        return passesBasicFilters(point, fullFilterConfig);
      });
    }

    // Apply TWS bin filtering if a specific bin is selected (not "ALL")
    const currentTwsBin = twsBin();
    if (currentTwsBin && currentTwsBin !== 'ALL') {
      const twsField = defaultChannelsStore.twsName();
      const targetBin = Number(currentTwsBin);
      if (!isNaN(targetBin)) {
        data = data.filter((d: any) => {
          // Try multiple field name variations (case-insensitive)
          const twsValue = d[twsField];
          if (twsValue !== undefined && twsValue !== null && !isNaN(Number(twsValue))) {
            // Round to nearest 5 knots to match binning (e.g., 5, 10, 15, 20, 25)
            const roundedTws = Math.round(Number(twsValue) / 5) * 5;
            return roundedTws === targetBin;
          }
          return false;
        });
      }
    }

    // Apply point of sail filtering if enabled
    if (showUwDw()) {
      const twaField = defaultChannelsStore.twaName();
      if (UwDw() === 'UPWIND') {
        const filtered = data.filter((d: PerformanceAggregatePoint) => Math.abs(d[twaField]) < 90);
        return applyExploreMapTimeScope([...filtered]);
      } else {
        const filtered = data.filter((d: PerformanceAggregatePoint) => Math.abs(d[twaField]) >= 90);
        return applyExploreMapTimeScope([...filtered]);
      }
    }
    return applyExploreMapTimeScope([...data]);
  });

  // Create a memo that tracks selectedEvents and cutEvents for box plot reactivity
  // This ensures box plots update when selection changes
  // NOTE: Must be defined AFTER getFilteredAggregates to avoid temporal dead zone error
  const boxPlotDataKey = createMemo(() => {
    const selEvents = selectedAggregateEvents();
    const cuts = cutEvents();
    const filtered = getFilteredAggregates();
    // Create a key that changes when selection or data changes
    return `${selEvents?.length ?? 0}-${cuts?.length ?? 0}-${filtered.length}`;
  });

  // Memoized filtered cloud - properly tracked within reactive context
  const filteredCloud = createMemo((): PerformanceCloudPoint[] => {
    let data = cloud();

    // Apply selection filtering if there are cut events
    const currentCuts = cutEvents();
    if (currentCuts.length > 0) {
      data = applyCutToRows(data, currentCuts);
    }

    // Apply client-side filters: STATE, RACE, LEG, GRADE (use filterStore like FleetPerformance / FleetPerformanceHistory)
    const races = selectedRacesAggregates();
    const legs = selectedLegsAggregates();
    const grades: number[] = [];
    const gradeValues = selectedGradesAggregates();
    if (Array.isArray(gradeValues) && gradeValues.length > 0) {
      grades.push(...gradeValues.map((g: string | number) => (typeof g === 'number' ? g : Number(g))).filter((n: number) => !isNaN(n) && (n >= 0 && n <= 3)));
    }
    const states: string[] = [];
    const stateValues = selectedStatesAggregates();
    if (Array.isArray(stateValues) && stateValues.length > 0) {
      states.push(...stateValues.map((s: string) => String(s)));
    }
    
    // Apply client-side filters if any are active
    if (races.length > 0 || legs.length > 0 || grades.length > 0 || states.length > 0) {
      const filterConfig = createFilterConfig(
        [], // twaStates
        races,
        legs,
        grades
      );
      
      // Add STATE filter to config (separate from twaStates)
      const fullFilterConfig = {
        ...filterConfig,
        twaStates: [],
        states: states, // State field filter (e.g., H0, H1, H2) - always client-side
      };
      
      const beforeCount = data.length;
      data = data.filter((point: any) => {
        // Data is normalized to lowercase fields (race_number, leg_number, grade, state) by processPerformanceData
        // passesBasicFilters expects normalized lowercase fields and handles type conversion internally
        const passes = passesBasicFilters(point, fullFilterConfig);
        return passes;
      });
      
      // Debug logging for STATE filter on cloud data
      if (states.length > 0) {
        const allStatesInCloud = Array.from(new Set(cloud().map((p: any) => p.state).filter((s: any) => s != null && s !== 'NONE')));
        const allStatesInCloudWithCase = Array.from(new Set(cloud().map((p: any) => {
          const s = p.state ?? p.State ?? p.STATE;
          return (s != null && s !== 'NONE') ? String(s) : null;
        }).filter((s: any) => s != null)));
        
        // Check a few sample points to see their state values
        const samplePoints = cloud().slice(0, 10).map((p: any) => ({
          state: p.state,
          State: p.State,
          STATE: p.STATE,
          hasState: p.state != null && p.state !== 'NONE',
          hasStateUpper: p.State != null && p.State !== 'NONE',
          hasStateAllCaps: p.STATE != null && p.STATE !== 'NONE',
          event_id: p.event_id
        }));
        
        // Count how many points have each state value
        const stateCounts: Record<string, number> = {};
        cloud().forEach((p: any) => {
          const s = p.state ?? p.State ?? p.STATE;
          if (s != null && s !== 'NONE') {
            const stateStr = String(s);
            stateCounts[stateStr] = (stateCounts[stateStr] || 0) + 1;
          }
        });
        
        logDebug(`[Performance] Cloud filtering: ${beforeCount} -> ${data.length} points`, {
          statesFilter: states,
          statesFilterLower: states.map(s => String(s).toLowerCase()),
          samplePointState: data.length > 0 ? data[0]?.state : 'no data after filter',
          samplePointStates: data.slice(0, 5).map((p: any) => p.state),
          allStatesInCloud: allStatesInCloud,
          allStatesInCloudWithCase: allStatesInCloudWithCase,
          stateCounts: stateCounts,
          sampleBeforeFilter: samplePoints,
          cloudDataLength: cloud().length,
          pointsWithNullState: cloud().filter((p: any) => (p.state == null || p.state === 'NONE') && (p.State == null || p.State === 'NONE') && (p.STATE == null || p.STATE === 'NONE')).length
        });
      }
    }

    // Apply point of sail filtering if enabled
    if (showUwDw()) {
      const twaField = defaultChannelsStore.twaName();
      if (UwDw() === 'UPWIND') {
        return applyExploreMapTimeScope(
          data.filter((d: PerformanceCloudPoint) => Math.abs(d[twaField]) < 90)
        );
      } else {
        return applyExploreMapTimeScope(
          data.filter((d: PerformanceCloudPoint) => Math.abs(d[twaField]) >= 90)
        );
      }
    }
    return applyExploreMapTimeScope(data);
  });

  // Non-reactive variable to hold the plain filtered cloud value
  // Components expect a plain array, not a memo, to avoid reactivity issues
  let filteredCloudValue: PerformanceCloudPoint[] = [];

  // Update the variable when the memo changes (within reactive context)
  createEffect(() => {
    filteredCloudValue = filteredCloud();
  });

  const getFilteredTargets = () => {
    if (showUwDw()) {
      if (UwDw() === 'UPWIND') {
        return targets().data.UPWIND;
      } else {
        return targets().data.DOWNWIND;
      }
    }

    return targets().data;
  };

  // Keyboard shortcuts: 'x' to clear selection, 'c' to cut selection
  // Keys 1-3: Grade selected events (handled by PerfScatter component)
  const handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || (target as HTMLElement).isContentEditable)) {
      return;
    }

    // Check if PerfSettings modal is visible
    const perfSettingsModal = document.querySelector('.pagesettings-modal');
    if (perfSettingsModal) {
      // Modal is visible, don't handle keyboard shortcuts
      return;
    }

    // 'x' key: Clear selection or clear cuts if no selection
    if (event.key === 'x' || event.key === 'X') {
      event.preventDefault();
      log('Performance: Clearing selection (x key pressed)');
      clearSelection({ domain: 'aggregate' });
      // Defer chart update to next frame for better responsiveness
      requestAnimationFrame(() => {
        setUpdateCharts(true);
      });
      return;
    }
    // 'h' key: Hide selected events (move to hidden list, then clear selection)
    if (event.key === 'h' || event.key === 'H') {
      event.preventDefault();
      const currentSelected = selectedAggregateEvents();
      if (currentSelected && currentSelected.length > 0) {
        log('Performance: Hiding selection (h key pressed)');
        hideSelectedEvents('aggregate');
        requestAnimationFrame(() => {
          setUpdateCharts(true);
        });
      }
      return;
    }
    // 'c' key: Cut selection (move selectedEvents to cutEvents)
    if (event.key === 'c' || event.key === 'C') {
      event.preventDefault();
      const currentSelected = selectedAggregateEvents();
      if (currentSelected && currentSelected.length > 0) {
        log('Performance: Cutting selection (c key pressed)');
        cutSelection('aggregate');
        // Defer chart update to next frame for better responsiveness
        requestAnimationFrame(() => {
          setUpdateCharts(true);
        });
      }
      return;
    }
  };

  // Fetch available targets (ispolar = 0, sorted by date_modified desc)
  const fetchAvailableTargets = async (): Promise<string[]> => {
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();

      if (!className || !projectId) {
        logError('Cannot fetch targets: missing class_name or project_id');
        return ['No Target'];
      }

      const url = `${apiEndpoints.app.targets}?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&isPolar=0`;
      logDebug(`[Performance] Fetching targets from: ${url}`);
      
      const response = await getData(url, new AbortController().signal);

      logDebug(`[Performance] Targets API response:`, { 
        success: response.success, 
        hasData: !!response.data, 
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A'
      });

      const targets: string[] = ['No Target']; // Always include "No Target" as first option

      if (response.success && response.data) {
        // Ensure response.data is an array
        if (!Array.isArray(response.data)) {
          logError('[Performance] Targets API returned non-array data:', response.data);
          return targets;
        }

        if (response.data.length === 0) {
          logDebug('[Performance] No targets found in API response');
          return targets;
        }

        // Sort by date_modified desc (API should already do this, but ensure it)
        const sorted = response.data.sort((a: any, b: any) => {
          const dateA = new Date(a.date_modified || 0).getTime();
          const dateB = new Date(b.date_modified || 0).getTime();
          return dateB - dateA; // Descending
        });
        
        // Filter out any targets without valid names
        const validTargetNames = sorted
          .map((t: any) => t?.name)
          .filter((name: any): name is string => typeof name === 'string' && name.trim().length > 0);
        
        if (validTargetNames.length > 0) {
          targets.push(...validTargetNames);
          logDebug(`[Performance] Found ${validTargetNames.length} valid targets:`, validTargetNames);
        } else {
          logWarn('[Performance] No valid target names found in API response');
        }
      } else {
        logWarn('[Performance] Targets API call failed or returned no data:', { 
          success: response.success, 
          message: (response as any).message 
        });
      }

      return targets;
    } catch (error) {
      logError('Error fetching available targets:', error as any);
      return ['No Target'];
    }
  };

  // Fetch target data by name
  const fetchTargetData = async (targetName: string): Promise<PerformanceTargetData> => {
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();

      if (!className || !projectId || !targetName) {
        logError('Cannot fetch target data: missing class_name, project_id, or targetName');
        return { name: '', data: {} };
      }

      const response = await getData(
        `${apiEndpoints.app.targets}/data?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&name=${encodeURIComponent(targetName)}&isPolar=0`,
        new AbortController().signal
      );

      if (response.success && response.data) {
        return {
          name: targetName,
          data: response.data || {}
        };
      }

      return { name: '', data: {} };
    } catch (error) {
      logError('Error fetching target data:', error as any);
      return { name: '', data: {} };
    }
  };

  // Handle target click - open modal
  const handleTargetClick = async () => {
    logDebug('Performance: handleTargetClick called');
    setShowTargetModal(true);
    logDebug('Performance: showTargetModal set to true');
    const targetsList = await fetchAvailableTargets();
    setAvailableTargets(targetsList);
    // Default to current target name, or "No Target" if no target is set
    const currentTarget = targets();
    const currentTargetName = currentTarget?.name || 'No Target';
    setSelectedTargetName(currentTargetName);
    setModalTargetColor(targetColor());
    logDebug('Performance: Modal should be visible now', { showTargetModal: showTargetModal(), availableTargets: targetsList.length });
  };

  // Save target name to persistent settings
  const saveTargetToPersistentSettings = async (
    targetName: string,
    color?: string
  ): Promise<void> => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          await savePerformanceTargetToPersistentSettings(
            currentUser.user_id,
            className,
            projectId,
            {
              targetName,
              ...(color !== undefined ? { targetColor: color } : {}),
            }
          );
          logDebug('Performance: Saved target to persistent settings', { targetName, color });
        }
      } catch (error: unknown) {
        logDebug('Performance: Error saving target to persistent settings:', error as any);
      }
    }
  };

  // Load target name from persistent settings
  const loadTargetFromPersistentSettings = async (): Promise<string | null> => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          const settings = await persistentSettingsService.loadSettings(
            currentUser.user_id,
            className,
            projectId
          );

          if (settings?.performanceTarget && typeof settings.performanceTarget === 'string') {
            const cleanedTarget = cleanQuotes(settings.performanceTarget);
            logDebug('Performance: Loaded target from persistent settings', cleanedTarget);
            return cleanedTarget;
          }
        }
      } catch (error: unknown) {
        logDebug('Performance: Error loading target from persistent settings:', error as any);
      }
    }
    return null;
  };

  // Save target name to sessionStorage (session persistence)
  const saveTargetToSessionStorage = (targetName: string, color?: string): void => {
    try {
      writePerformanceTargetSession({
        targetName,
        ...(color !== undefined ? { targetColor: color } : {}),
      });
      logDebug('Performance: Saved target to sessionStorage', { targetName, color });
    } catch (error) {
      logDebug('Performance: Error saving target to sessionStorage:', error);
    }
  };

  const loadTargetColorFromSessionStorage = (): string | null => {
    const session = readPerformanceTargetSession();
    return session.targetColor ?? null;
  };

  const loadTargetColorFromPersistentSettings = async (): Promise<string | null> => {
    const currentUser = user();
    if (!currentUser?.user_id) return null;
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) return null;
    return loadPerformanceTargetColorFromPersistentSettings(
      currentUser.user_id,
      className,
      projectId
    );
  };

  // Load target name from sessionStorage (session persistence)
  const loadTargetFromSessionStorage = (): string | null => {
    try {
      const session = readPerformanceTargetSession();
      if (session.targetName && typeof session.targetName === 'string') {
        logDebug('Performance: Loaded target from sessionStorage', session.targetName);
        return session.targetName;
      }
    } catch (error) {
      logDebug('Performance: Error loading target from sessionStorage:', error);
    }
    return null;
  };

  // Helper function to find matching target name in available targets list
  // Handles cases where target names may have "_target" suffix
  const findMatchingTarget = (targetName: string, availableTargets: string[]): string | undefined => {
    if (!targetName || availableTargets.length === 0) {
      return undefined;
    }
    
    // First try exact match
    if (availableTargets.includes(targetName)) {
      return targetName;
    }
    
    // Try with "_target" suffix appended
    const withSuffix = `${targetName}_target`;
    if (availableTargets.includes(withSuffix)) {
      return withSuffix;
    }
    
    // Try finding a target that matches when "_target" is stripped from available targets
    const match = availableTargets.find(target => {
      // Remove "_target" suffix if present and compare
      const baseName = target.endsWith('_target') ? target.slice(0, -7) : target;
      return baseName === targetName;
    });
    
    return match;
  };

  // Fetch default target from project object
  const fetchDefaultTargetFromProjectObject = async (): Promise<string | null> => {
    try {
      // Resolve date
      let dateToUse: string;
      const selectedDateValue = selectedDate();
      if (selectedDateValue) {
        dateToUse = selectedDateValue;
      } else {
        const datasetId = typeof selectedDatasetId === 'function' ? selectedDatasetId() : 0;
        if (datasetId > 0) {
          dateToUse = await performanceDataService.fetchDatasetDate();
        } else {
          return null; // No date available
        }
      }
      
      if (!dateToUse) {
        return null;
      }
      
      // Strip any quotes that might have been added during storage/serialization
      dateToUse = cleanQuotes(dateToUse);
      
      // Try both date formats: YYYY-MM-DD (original) and YYYYMMDD (converted)
      // Database stores dates as date type which uses YYYY-MM-DD format
      const dateFormats = dateToUse.includes('-') 
        ? [dateToUse, dateToUse.replace(/-/g, '')] // Try YYYY-MM-DD first, then YYYYMMDD
        : [dateToUse]; // Already in YYYYMMDD format
      
      let response: any = null;
      let dateUsed: string = '';
      
      // Try each date format until we find a match
      for (const formattedDate of dateFormats) {
        response = await getData(
          `${apiEndpoints.app.projects}/object?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&date=${encodeURIComponent(formattedDate)}&object_name=target`
        );
        
        logDebug('Performance: Project object fetch response', {
          success: response.success,
          hasData: !!response.data,
          statusCode: (response as any).statusCode,
          dateUsed: formattedDate,
          dateFormat: formattedDate.includes('-') ? 'YYYY-MM-DD' : 'YYYYMMDD'
        });
        
        // If found, break out of loop
        if (response.success && response.data && (response as any).statusCode !== 204) {
          dateUsed = formattedDate;
          break;
        }
      }
      
      // If not found with specific date, try default date '1970-01-01' (try both formats)
      let isNotFound = !response || !response.success || !response.data || (response as any).statusCode === 204;
      if (isNotFound) {
        const defaultDates = ['1970-01-01', '19700101'];
        for (const defaultDate of defaultDates) {
          logDebug('Performance: Trying default date for project object', defaultDate);
          response = await getData(
            `${apiEndpoints.app.projects}/object?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&date=${encodeURIComponent(defaultDate)}&object_name=target`
          );
          logDebug('Performance: Project object fetch response (default date)', {
            success: response.success,
            hasData: !!response.data,
            statusCode: (response as any).statusCode,
            dateUsed: defaultDate
          });
          
          // If found, break out of loop
          if (response.success && response.data && (response as any).statusCode !== 204) {
            dateUsed = defaultDate;
            isNotFound = false;
            break;
          }
        }
      }

      // If still not found, use last known project_object with object_name 'target'
      if (isNotFound) {
        logDebug('Performance: No target for date, fetching last known project object (object_name=target)');
        const latestResponse = await getData(
          `${apiEndpoints.app.projects}/object/latest?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&object_name=target`
        );
        if (latestResponse?.success && latestResponse.data && (latestResponse as any).statusCode !== 204) {
          const latestData = latestResponse.data as { json?: unknown; date?: string };
          const rawJson = latestData?.json;
          if (rawJson != null) {
            response = { success: true, data: typeof rawJson === 'string' ? rawJson : rawJson };
            dateUsed = latestData.date ? String(latestData.date) : 'latest';
            isNotFound = false;
            logDebug('Performance: Using last known target from project_objects', { dateUsed });
          }
        }
      }
      
      if (response && response.success && response.data) {
        try {
          // Parse JSON - handle both string and object responses
          let targetObj: any;
          if (typeof response.data === 'string') {
            targetObj = JSON.parse(response.data);
          } else {
            targetObj = response.data;
          }
          
          logDebug('Performance: Parsed project object', {
            targetObj,
            type: typeof targetObj,
            isArray: Array.isArray(targetObj),
            keys: typeof targetObj === 'object' && targetObj !== null ? Object.keys(targetObj) : []
          });
          
          // Handle array response - get first element if it's an array
          let obj: any;
          if (Array.isArray(targetObj)) {
            if (targetObj.length > 0) {
              obj = targetObj[0];
            } else {
              logWarn('Performance: Project object is an empty array', { targetObj });
              return null;
            }
          } else {
            obj = targetObj;
          }
          
          // Check for both 'name' and 'name_target' fields
          const targetName = obj?.name || obj?.name_target;
          if (targetName && typeof targetName === 'string') {
            logDebug('Performance: Fetched default target from project object', { targetName, dateUsed });
            return targetName;
          } else {
            logWarn('Performance: Project object exists but has no name field', { 
              targetObj, 
              obj,
              availableKeys: obj ? Object.keys(obj) : []
            });
          }
        } catch (parseError) {
          logError('Performance: Error parsing project object JSON:', parseError, { responseData: response.data });
        }
      } else {
        logWarn('Performance: Project object not found or fetch failed', {
          success: response?.success,
          statusCode: (response as any)?.statusCode,
          message: (response as any)?.message,
          dateUsed: dateUsed || dateToUse
        });
      }
    } catch (error) {
      logError('Performance: Error fetching default target from project object:', error);
    }
    return null;
  };

  // Handle target selection from modal
  const handleTargetSelect = async () => {
    const targetName = selectedTargetName();
    const nextTargetColor = modalTargetColor();

    setShowTargetModal(false);
    setTargetColor(nextTargetColor);

    if (!targetName || targetName === 'No Target') {
      setTargets({ name: '', data: {} });
      forcePerformanceChartUpdate(setUpdateCharts);
      logDebug('Performance: Cleared target (No Target selected)');
      await saveTargetToPersistentSettings('', nextTargetColor);
      saveTargetToSessionStorage('', nextTargetColor);
      return;
    }

    const targetData = await fetchTargetData(targetName);
    setTargets(targetData);
    forcePerformanceChartUpdate(setUpdateCharts);
    logDebug(`Performance: Selected target "${targetName}"`, { color: nextTargetColor });
    await saveTargetToPersistentSettings(targetName, nextTargetColor);
    saveTargetToSessionStorage(targetName, nextTargetColor);
  };

  onMount(() => {
    // Stale selectedEvents from other routes: clear so scatter/box don’t show a misleading brush state.
    clearSelection({ preserveFilters: true, immediate: true, domain: 'aggregate' });

    loadFiltersFromSession();

    window.addEventListener('keydown', handleKeyDown);
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: 'Performance',
      scaleToWidth: true
    });

    onCleanup(() => {
      cleanupScaling();
      window.removeEventListener('keydown', handleKeyDown);
    });

    void (async () => {
    await logPageLoad('Performance.tsx', 'Performance Analysis Report');

    // Wait for store to have project/class (e.g. after rehydration on reload)
    let className = selectedClassName();
    let projectId = selectedProjectId();
    for (let i = 0; i < 30 && (!className || !projectId); i++) {
      await new Promise(r => setTimeout(r, 100));
      className = selectedClassName();
      projectId = selectedProjectId();
    }

    // Restore the previously displayed baselines. Deliberately after the rehydration wait above:
    // the hook needs class and project to look a baseline up and returns silently without them, so
    // restoring at the top of onMount would no-op on a cold reload. Not awaited — charts mount
    // lazily through PerformanceChartViewportSlot, so any that scroll in later pick the overlays up
    // on their first draw, and anything already drawn is covered by onApplied.
    void Promise.all(baselineSlots.map((slot) => slot.restoreSavedBaseline()));

    // Resolve dataset date for project-object lookup (dataset report)
    let dateForFilters: string | null = null;
    const currentDatasetId = typeof selectedDatasetId === 'function' ? selectedDatasetId() : 0;
    if (currentDatasetId > 0 && className && projectId) {
      try {
        const response = await getData(
          `${apiEndpoints.app.datasets}/info?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&dataset_id=${encodeURIComponent(currentDatasetId)}`
        );
        if (response.success && response.data?.date) {
          let dateStr = response.data.date;
          if (dateStr.length === 8 && !dateStr.includes('-')) {
            dateStr = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
          }
          dateForFilters = dateStr;
        }
      } catch (_) { /* empty */ }
    }

    // Load grade/state: project default first (by date), then user persistent if no project filters
    const projectFilters = (className && projectId) ? await getProjectPerformanceFilters(className, projectId, dateForFilters ?? undefined) : null;
    if (projectFilters === null) {
      await loadGradeStateFiltersFromPersistentSettings();
    }

    if (!filterGrades() || filterGrades().trim() === '') {
      setFilterGrades('2,3');
      logDebug('Performance: Set default grade filter to 2,3');
    }
    if (!filterState() || filterState().trim() === '') {
      logDebug('Performance: State filter empty = ALL (no state filter)');
    }

    if (projectFilters !== null) {
      if (projectFilters.grades !== undefined) {
        setFilterGrades(cleanQuotes(projectFilters.grades ?? ''));
        logDebug('Performance: Applied grade filter from project default', projectFilters.grades);
      }
      if (projectFilters.state !== undefined) {
        setFilterState(cleanQuotes(projectFilters.state ?? ''));
        logDebug('Performance: Applied state filter from project default', projectFilters.state);
      }
    }

    const initialGrades = filterGrades().trim();
    const initialStates = filterState().trim();
    const gradesArr = initialGrades ? initialGrades.split(',').map(g => parseInt(g.trim())).filter(g => !isNaN(g) && (g >= 0 && g <= 3)) : [];
    const statesArr = initialStates && initialStates.toUpperCase() !== 'ALL' ? initialStates.split(',').map(s => s.trim().toUpperCase()).filter(s => s === 'H0' || s === 'H1' || s === 'H2') : [];
    setSelectedGradesAggregates(gradesArr.map(String));
    setSelectedStatesAggregates(statesArr);

    let savedTargetName = loadTargetFromSessionStorage();
    if (!savedTargetName) {
      savedTargetName = await loadTargetFromPersistentSettings();
    }

    let savedTargetColor = loadTargetColorFromSessionStorage();
    if (!savedTargetColor) {
      savedTargetColor = await loadTargetColorFromPersistentSettings();
    }
    if (savedTargetColor) {
      setTargetColor(savedTargetColor);
    }

    let targetNameToPreserve: string | undefined = undefined;
    const cleanedTargetName = savedTargetName ? cleanQuotes(savedTargetName) : null;
    if (cleanedTargetName && cleanedTargetName !== 'No Target') {
      const targetsList = await fetchAvailableTargets();
      const matchingTargetName = findMatchingTarget(cleanedTargetName, targetsList);
      if (matchingTargetName) {
        const targetData = await fetchTargetData(matchingTargetName);
        setTargets(targetData);
        targetNameToPreserve = matchingTargetName;
        logDebug(`Performance: Loaded saved target "${matchingTargetName}" from persistent settings (matched from "${cleanedTargetName}")`);
      } else {
        logDebug(`Performance: Saved target "${cleanedTargetName}" no longer exists, skipping load`);
      }
    } else {
      const defaultTargetNameRaw = await fetchDefaultTargetFromProjectObject();
      const defaultTargetName = defaultTargetNameRaw ? cleanQuotes(defaultTargetNameRaw) : null;
      if (defaultTargetName) {
        const targetsList = await fetchAvailableTargets();
        const matchingTargetName = findMatchingTarget(defaultTargetName, targetsList);
        
        if (matchingTargetName) {
          const targetData = await fetchTargetData(matchingTargetName);
          setTargets(targetData);
          targetNameToPreserve = matchingTargetName;
          await saveTargetToPersistentSettings(matchingTargetName);
          logDebug(`Performance: Loaded default target "${matchingTargetName}" from project object (matched from "${defaultTargetName}") and saved to persistent settings`);
        } else {
          logWarn(`Performance: Default target "${defaultTargetName}" from project object not found in available targets. Available targets:`, targetsList);
        }
      }
    }

    if (dateForFilters) {
      logDebug(`[Performance] onMount: Setting filterStore dates to dataset date: ${dateForFilters}`);
      setStartDate(dateForFilters);
      setEndDate(dateForFilters);
    }

    await requestInitializeCharts(targetNameToPreserve, true);
    })();
  });

  // Cleanup abort controller and color save timeout on unmount
  onCleanup(() => {
    if (timesController) {
      timesController.abort();
    }
    if (colorSaveTimeout) {
      clearTimeout(colorSaveTimeout);
      colorSaveTimeout = null;
    }
  });

  const handleLegendClick = (legendItem: string) => {
    logDebug('Performance: handleLegendClick called', { legendItem, colorField: color(), aggregatesCount: aggregates().length });
    // Use full aggregates, not filtered by cuts, so selection can span all events
    const fullAggregates = aggregates();
    const currentColorField = color();
    const clickedItem = String(legendItem);
    const clickedItemLower = clickedItem.toLowerCase();
    
    // Map color field names to actual data field names
    // Note: All metadata fields are normalized to lowercase (matches unifiedDataStore normalization)
    const getFieldName = (colorField: string): string => {
      const fieldMap: Record<string, string> = {
        'TACK': 'tack',
        'GRADE': 'grade',
        'RACE': 'race_number',
        'LEG': 'leg_number',
        'CONFIG': 'config',
        'CREW': 'crew',
        'TESTS': 'tests',
        'YEAR': 'year',
        'EVENT': 'event',
        'SOURCE_NAME': 'source_name',
        'STATE': 'state',
        'SELECTIONS': 'selection'
      };
      return fieldMap[colorField] || colorField;
    };
    
    const actualFieldName = getFieldName(currentColorField);
    
    // Filter by current color field from the FULL aggregates (not filtered by cuts)
    // Only apply point of sail filter if enabled
    let dataSource = fullAggregates;
    if (showUwDw()) {
      const twaField = defaultChannelsStore.twaName();
      if (UwDw() === 'UPWIND') {
        dataSource = fullAggregates.filter((d: PerformanceAggregatePoint) => Math.abs(d[twaField]) < 90);
      } else {
        dataSource = fullAggregates.filter((d: PerformanceAggregatePoint) => Math.abs(d[twaField]) >= 90);
      }
    }
    
    const legendEntryIds = dataSource
      .filter((a: PerformanceAggregatePoint) => {
        // Get the field value from the data point
        const fieldValue = a[actualFieldName];
        
        // Handle SOURCE_NAME specially (case-insensitive comparison)
        if (currentColorField === 'SOURCE_NAME') {
          // Check all possible field name variations
          const sourceName = String(
            fieldValue || 
            a.source_name || 
            a.sourceName || 
            a.Source_name || 
            a.SOURCE_NAME || 
            'Unknown'
          ).toLowerCase().trim();
          const clickedSourceLower = clickedItemLower.trim();
          return sourceName === clickedSourceLower;
        }
        
        // Handle RACE specially - need to handle TRAINING and numeric races
        if (currentColorField === 'RACE') {
          const raceValue = fieldValue !== undefined && fieldValue !== null ? fieldValue : null;
          const clickedStr = clickedItem;
          
          // Handle TRAINING: clicked item is 'TRAINING' and field value is 'TRAINING', -1, or '-1'
          if (clickedStr === 'TRAINING' || clickedStr === 'training') {
            return raceValue === 'TRAINING' || raceValue === 'training' || raceValue === -1 || raceValue === '-1';
          }
          
          // Handle numeric races: try to match as numbers
          const raceNum = typeof raceValue === 'number' ? raceValue : Number(raceValue);
          const clickedNum = Number(clickedStr);
          if (!isNaN(raceNum) && !isNaN(clickedNum)) {
            return raceNum === clickedNum;
          }
          
          // Try string match
          const raceValueStr = String(raceValue);
          if (raceValueStr === clickedStr) return true;
          if (raceValueStr.toLowerCase() === clickedStr.toLowerCase()) return true;
          
          return false;
        }
        
        // Handle STATE specially (case-insensitive comparison)
        if (currentColorField === 'STATE') {
          const stateValue = fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : null;
          if (stateValue) {
            return stateValue.toLowerCase() === clickedItemLower;
          }
          return false;
        }
        
        // For other fields, compare values (handle both string and number)
        const fieldValueStr = fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : null;
        const clickedStr = clickedItem;
        
        // Try exact match first
        if (fieldValueStr === clickedStr) return true;
        
        // Try number comparison if both are numeric
        const fieldNum = Number(fieldValueStr);
        const clickedNum = Number(clickedStr);
        if (!isNaN(fieldNum) && !isNaN(clickedNum) && fieldNum === clickedNum) return true;
        
        // Try case-insensitive string match
        if (fieldValueStr && clickedStr && fieldValueStr.toLowerCase() === clickedStr.toLowerCase()) return true;
        
        return false;
      })
      .map((a: PerformanceAggregatePoint) => Number(a.event_id))
      .filter((id): id is number => !isNaN(id) && id !== null && id !== undefined);

    if (legendEntryIds.length === 0) {
      log('Performance: Legend click on', legendItem, '- no matching IDs found');
      setUpdateCharts(true);
      return;
    }

    // Get current selection
    const currentSelected = selectedAggregateEvents();
    const currentSelectedSet = new Set(currentSelected);
    
    // Check if all legend entry IDs are already selected
    const allSelected = legendEntryIds.every(id => currentSelectedSet.has(id));
    
    if (allSelected) {
      // All IDs are selected → remove all legend entry IDs
      const updatedSelection = currentSelected.filter(id => !legendEntryIds.includes(id));
      setSelectedAggregateEvents(updatedSelection);
      setHasSelection(updatedSelection.length > 0);
      log('Performance: Legend click on', legendItem, '- deselected all', legendEntryIds.length, 'event IDs');
    } else {
      // Not all IDs are selected → select all legend entry IDs (merge with existing)
      const updatedSelection = Array.from(new Set([...currentSelected, ...legendEntryIds]));
      setSelectedAggregateEvents(updatedSelection);
      setHasSelection(updatedSelection.length > 0);
      log('Performance: Legend click on', legendItem, '- selected all', legendEntryIds.length, 'event IDs');
    }

    setTriggerSelection(true);
    // Simple chart update
    setUpdateCharts(true);
  };

  // Populate selectedRanges so explore map highlights performance selections (selectionStore skips getEventTimeRanges on this page).
  let lastPerformanceRangesSig = "";
  createEffect(() => {
    const ids = selectedAggregateEvents();
    if (!Array.isArray(ids) || ids.length === 0) {
      lastPerformanceRangesSig = "";
      // Clear explicitly; the store's follow-up skips its clear while a brush is active.
      if (untrack(() => selectedRanges()).length > 0) {
        setSelectedRanges([]);
      }
      return;
    }

    const aggRows = aggregates() as Array<Record<string, unknown>>;
    const resolveColor = createPerformanceRangeColorResolver({
      colorField: color(),
      groups: groups(),
    });

    // Per-id build: the async getEventTimes rows lag the click, and pairing them with an all-or-nothing
    // row fallback dropped the ids they hadn't covered yet.
    const ranges = buildSelectedRangesForEvents({
      eventIds: ids,
      selectionData: selection(),
      lookupRows: aggRows,
      resolveColor,
    });
    if (ranges.length === 0) return;

    const sig = selectionRangesSignatureForEvents(ids, ranges);
    if (sig === lastPerformanceRangesSig) return;
    lastPerformanceRangesSig = sig;
    setSelectedRanges(ranges);
  });

  // Effect to fetch event time data when events are selected
  let timesController: AbortController | null = null;
  createEffect(() => {
    const eventList = selectedAggregateEvents();
    if (eventList.length === 0) return;

    const datasetId = selectedDatasetId();
    if (!datasetId || datasetId <= 0) {
      logDebug('Performance: Skipping times fetch (dataset_id required)');
      return;
    }

    if (timesController) {
      timesController.abort();
    }

    timesController = new AbortController();
    const signal = timesController.signal;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    void runEventTimesSelectionSync({
      fetchErrorLabel: "Error fetching event times:",
      applyErrorLabel: "Error applying event times selection:",
      fetchTimes: () =>
        getEventTimes(
          {
            class_name: className,
            project_id: projectId,
            dataset_id: datasetId,
            event_list: eventList,
          },
          signal
        ),
      applySelection: (result) => {
        const response = result as { success?: boolean; data?: unknown };
        if (response.success && response.data) {
          setSelection(response.data);
        }
      },
    }).finally(() => {
      timesController = null;
    });
  });

  // Fallback: clear mode-switching overlay after 3s if chart never reports rendered
  createEffect(() => {
    if (!modeSwitching()) return;
    const t = setTimeout(() => setModeSwitching(false), 3000);
    onCleanup(() => clearTimeout(t));
  });

  return (
    <div id='media-container' class="performance-page">
      <Show when={!loading() && !isLoading()} fallback={<Loading />}>
        <Show when={requestError()}>
          <div class="container relative performance-load-error-page">
            <div class="performance-legend-section">
              <div class="performance-settings-trigger">
                <PerfSettings
                  pageMode="dataset"
                  useIconTrigger={true}
                  colorOptions={colors()}
                  selectedColor={() => color()}
                  onColorChange={handleColorChange}
                  selectedXAxis={() => xAxis()}
                  onXAxisChange={(axis) => handleAxisChange(axis)}
                  selectedPlotType={() => plotType()}
                  onPlotTypeChange={handlePlotTypeChange}
                  cloudDataOptions={['None', '1Hz Scatter', 'Recent History', 'Fleet Data', 'Season 5']}
                  selectedCloudData={() => {
                    const ct = cloudType();
                    if (ct === 'Latest') return '1Hz Scatter';
                    return ct;
                  }}
                  onCloudDataChange={(value) => {
                    const mapping: Record<string, string> = {
                      'None': 'None',
                      '1Hz Scatter': 'Latest',
                      'Recent History': 'Recent History',
                      'Fleet Data': 'Fleet Data',
                      'Season 5': 'Season 5'
                    };
                    const newCloudType = mapping[value] || 'None';
                    setCloudType(newCloudType);
                    setUpdateCharts(true);
                  }}
                  filterGrades={() => filterGrades()}
                  onFilterGradesChange={(value) => {
                    setFilterGrades(value);
                    saveGradeStateFiltersToPersistentSettings();
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                  filterCrew={() => filterCrew()}
                  onFilterCrewChange={(value) => {
                    setFilterCrew(value);
                  }}
                  filterState={() => filterState()}
                  onFilterStateChange={(value) => {
                    setFilterState(value);
                    saveGradeStateFiltersToPersistentSettings();
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                  selectedTrainingRacing={selectedTrainingRacing()}
                  onTrainingRacingFilterChange={(value) => {
                    setSelectedTrainingRacing(value);
                    saveGradeStateFiltersToPersistentSettings();
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                  showTimeline={() => showTimeline()}
                  onTimelineChange={handleTimelineChange}
                  onApplyFilters={() => {
                    const currentTargetName = targets()?.name;
                    const targetToPreserve = currentTargetName && currentTargetName !== '' ? currentTargetName : undefined;
                    void requestInitializeCharts(targetToPreserve, true);
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                />
              </div>
            </div>
            <div class="performance-load-error">
              <div class="performance-load-error-icon" aria-hidden="true">
                <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <h3 class="performance-load-error-title">Error Loading Performance Data</h3>
              <p class="performance-load-error-message">{requestError()}</p>
              <p class="performance-load-error-hint">Open page settings (gear) to clear or fix filters, then Retry.</p>
              <button
                type="button"
                class="performance-load-error-retry"
                onClick={() => requestInitializeCharts(undefined, true)}
              >
                Retry
              </button>
            </div>
          </div>
        </Show>
        <Show when={updateCharts() && !requestError()}>
          <div class="container relative">
            {/* Fixed legend section - stays at top */}
            <div class="performance-legend-section">
              {/* FiSettings button in upper left */}
              <div class="performance-settings-trigger">
                <PerfSettings
                  pageMode="dataset"
                  useIconTrigger={true}
                  colorOptions={colors()}
                  selectedColor={() => color()}
                  onColorChange={handleColorChange}
                  selectedXAxis={() => xAxis()}
                  onXAxisChange={(axis) => handleAxisChange(axis)}
                  selectedPlotType={() => plotType()}
                  onPlotTypeChange={handlePlotTypeChange}
                  cloudDataOptions={['None', '1Hz Scatter', 'Recent History', 'Fleet Data', 'Season 5']}
                  selectedCloudData={() => {
                    const ct = cloudType();
                    if (ct === 'Latest') return '1Hz Scatter';
                    return ct;
                  }}
                  onCloudDataChange={(value) => {
                    const mapping: Record<string, string> = {
                      'None': 'None',
                      '1Hz Scatter': 'Latest',
                      'Recent History': 'Recent History',
                      'Fleet Data': 'Fleet Data',
                      'Season 5': 'Season 5'
                    };
                    const newCloudType = mapping[value] || 'None';
                    setCloudType(newCloudType);
                    getCurrentProjectDatasetIds().then(({ project_id, dataset_id }) => {
                      logActivity(project_id, dataset_id, 'Performance.tsx', 'Performance Report', `Cloud type changed to ${newCloudType}`);
                    });
                    setUpdateCharts(true);
                  }}
                  filterGrades={() => filterGrades()}
                  onFilterGradesChange={(value) => {
                    setFilterGrades(value);
                    saveGradeStateFiltersToPersistentSettings();
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                  filterCrew={() => filterCrew()}
                  onFilterCrewChange={(value) => {
                    setFilterCrew(value);
                  }}
                  filterState={() => filterState()}
                  onFilterStateChange={(value) => {
                    setFilterState(value);
                    saveGradeStateFiltersToPersistentSettings();
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                  setRaceOptions={(_races) => {
                    // PerfSettings will update filterStore internally
                  }}
                  setLegOptions={(_legs) => {
                    // PerfSettings will update filterStore internally
                  }}
                  selectedTrainingRacing={selectedTrainingRacing()}
                  onTrainingRacingFilterChange={(value) => {
                    setSelectedTrainingRacing(value);
                    saveGradeStateFiltersToPersistentSettings();
                    setFilterTrigger(prev => prev + 1);
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                  showTimeline={() => showTimeline()}
                  onTimelineChange={handleTimelineChange}
                  onApplyFilters={() => {
                    log('Performance: onApplyFilters CALLED - triggering filter recalculation');
                    // STATE and CREW are applied in SQL, so a change needs a real refetch.
                    const needsRefetch =
                      filterState() !== lastFetchedStateFilter ||
                      filterCrew() !== lastFetchedCrewFilter;
                    if (needsRefetch) {
                      log('Performance: API filter changed, refetching aggregates', {
                        stateFrom: lastFetchedStateFilter,
                        stateTo: filterState(),
                        crewFrom: lastFetchedCrewFilter,
                        crewTo: filterCrew(),
                      });
                      const currentTargetName = targets()?.name;
                      const targetToPreserve = currentTargetName && currentTargetName !== '' ? currentTargetName : undefined;
                      void requestInitializeCharts(targetToPreserve, true);
                    }
                    setFilterTrigger(prev => {
                      const next = prev + 1;
                      log('Performance: filterTrigger incremented', { prev, next });
                      return next;
                    });
                    handleColorChange(color(), true);
                    setUpdateCharts(true);
                  }}
                />
              </div>
              {/* Header section with controls and legend - legend beside dropdowns, centered in remaining space */}
              <div class="flex w-full items-center gap-x-2">
                <div class="flex shrink-0 gap-x-2 pt-2 pl-2 items-center" style="margin-left: 55px;">
                  <Show when={showUwDw()}>
                    <DropDownButton
                      options={["UPWIND", "DOWNWIND"]}
                      defaultText={UwDw()}
                      handleSelection={handleUwDwChange}
                      smallLabel="Uw/Dw"
                      size="medium"
                    />
                  </Show>
                  <DropDownButton
                    options={colors()}
                    defaultText={color()}
                    handleSelection={handleColorChange}
                    smallLabel="Color"
                    size="medium"
                  />
                  <Show when={plotType() === "Box"}>
                    <DropDownButton
                      options={twsBinOptions()}
                      defaultText={twsBin()}
                      handleSelection={handleTwsBinChange}
                      smallLabel="TWS Bin"
                      size="medium"
                    />
                  </Show>
                </div>
                <div class="legend-center-wrapper flex-1 flex items-center justify-center min-w-0 pt-2">
                  <Legend
                    elementId="legend-container"
                    target_info={targets()}
                    cloudType={cloudType()}
                    groups={groups()}
                    onTargetClick={handleTargetClick}
                    targetColor={showTargetModal() ? modalTargetColor() : targetColor()}
                    baselines={baselineLegendEntries()}
                    color={color()}
                    click={handleLegendClick}
                    filterSummary={<PerformanceFilterSummary filterGrades={filterGrades()} filterState={filterState()} trainingRacing={selectedTrainingRacing()} />}
                  />
                </div>
              </div>
              {/* Timeline component - min-width: 0 so it can shrink with window in flex layouts */}
              <Show when={showTimeline()}>
                <div class="performance-timeline-slot">
                  <ScatterTimeseries selectionDomain="aggregate"
                    key={`timeline-${selectedTrainingRacing() || 'all'}-${getFilteredAggregatesAVG().length}`}
                    aggregates={getFilteredAggregatesAVG()}
                    color={color()}
                    groups={groups()}
                    isHistoryPage={false}
                    uwDw={UwDw()}
                    showUwDw={showUwDw()}
                  />
                </div>
              </Show>
            </div>
            {/* Scrollable charts container - inner div is the scroll viewport so charts scroll correctly */}
            <div class="performance-charts-scroll-container">
              <div
                class="performance-charts-scroll-inner"
                style="position: relative;"
                ref={(el) => {
                  scrollInnerEl = el instanceof HTMLElement ? el : undefined;
                }}
              >
                <Show when={modeSwitching()}>
                  <Loading message="Updating charts..." fullScreen={false} />
                </Show>
              <Show when={!zoom()}>
                <Show when={charts().length > 0} fallback={
                    <div class="flex flex-col items-center justify-center h-screen min-h-[500px] text-center p-8">
                      <div class="mb-6">
                        <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                        </svg>
                        <h3 class="text-xl font-semibold text-gray-700 mb-2">No Charts Available</h3>
                        <p class="text-gray-500 mb-6">Would you like to add one?</p>
                      </div>
                      <button
                        onClick={() => { window.location.href = buildPerformanceBuilderUrl(); }}
                        class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-md hover:shadow-lg"
                      >
                        Add Chart
                      </button>
                    </div>
                  }>
                    <div class="target-container">
                      <For each={chartGroups()}>
                        {(group) => (
                          <div class="group-container">
                            <div class="break">
                              <h2>{group.name}</h2>
                            </div>
                            <Show when={group.additionalText}>
                              <div style="font-size: 12px; text-align: center; margin-top: 4px; margin-bottom: 8px;">
                                {group.additionalText}
                              </div>
                            </Show>
                            <div class="target-plots">
                              <For each={group.charts[0].series}>
                                {(series) => (
                                    <PerformanceChartViewportSlot searchTitle={series.yaxis?.name ?? series.yaxis?.dataField ?? ""} searchDetail={(group as { name?: string }).name ?? ""}>
                                      <Show when={plotType() === "Scatter"}>
                                        <AdvancedScatter selectionDomain="aggregate"
                                          key={`scatter-${series.xaxisValue || xAxis()}-${series.yaxis.name}-${series.xType}-${series.yType}`}
                                          xaxis={series.xaxisValue || xAxis()}
                                          yaxis={series.yaxis.dataField || series.yaxis.name}
                                          taxis={series.yaxisTarget?.dataField || series.yaxisTarget?.name || ''}
                                          aggregate={series.yType || 'AVG'}
                                          filters={group.charts[0].filters}
                                          aggregates={getFilteredAggregates()}
                                          aggregatesAVG={getFilteredAggregatesAVG()}
                                          aggregatesSTD={getFilteredAggregatesSTD()}
                                          aggregatesAAV={getFilteredAggregatesAAV()}
                                          xType={series.xType || 'AVG'}
                                          yType={series.yType || 'AVG'}
                                          cloud={(series.xType || 'AVG').toUpperCase() === 'AVG' && (series.yType || 'AVG').toUpperCase() === 'AVG' ? filteredCloudValue : []}
                                          targets={getFilteredTargets()}
                                          targetName={targets()?.name || ''}
                                          targetColor={targetColor()}
                                          baselines={baselineOverlays()}
                                          uwDw={UwDw()}
                                          updateCharts={updateCharts()}
                                          color={color()}
                                          groups={groups()}
                                          mouseID={mouseID()}
                                          setMouseID={setMouseID}
                                          selectedChart={selectedChart()}
                                          handleZoom={handleZoom}
                                          zoom={zoom()}
                                          selectedEvents={selectedAggregateEvents()}
                                          hasCutData={cutEvents().length > 0}
                                          onDataUpdate={() => {
                                            const currentTargetName = targets()?.name;
                                            const targetToPreserve = currentTargetName && currentTargetName !== '' ? currentTargetName : undefined;
                                            requestInitializeCharts(targetToPreserve, true);
                                          }}
                                          onChartRendered={() => requestAnimationFrame(() => setModeSwitching(false))}
                                          infoType={series.info_type}
                                          infoMessage={series.info_message ?? ""}
                                          onLocalSelectionAssign={onLocalPerformanceSelectionAssign}
                                        />
                                      </Show>
                                      <Show when={plotType() === "Box"}>
                                        <PerformanceBoxPlotChart
                                          group={group}
                                          series={series}
                                          boxPlotDataKey={boxPlotDataKey}
                                          color={color}
                                          groups={groups}
                                          xAxis={xAxis}
                                          getFilteredAggregates={getFilteredAggregates}
                                          getFilteredAggregatesAVG={getFilteredAggregatesAVG}
                                          selectedEvents={selectedAggregateEvents}
                                          handleZoom={handleZoom}
                                          zoom={zoom}
                                        />
                                      </Show>
                                    </PerformanceChartViewportSlot>
                                  )}
                              </For>
                            </div>
                          </div>
                        )}
                      </For>
                      {/* Spacer div to ensure last chart is fully visible when scrolling */}
                      <div 
                        class="performance-scroll-spacer"
                        style={{
                          height: "450px",
                          width: "100%",
                          "flex-shrink": "0"
                        }}
                      />
                    </div>
                  </Show>
              </Show>
              <Show when={zoom()}>
                <div class="zoom-container" style={{ "min-height": showTimeline() ? "750px" : "800px", "margin-top": "50px" }}>
                  <div class="flex w-full h-full" style={{ height: showTimeline() ? "750px" : "800px", width: "calc(100% - 25px)" }}>
                    <AdvancedScatter selectionDomain="aggregate"
                      xaxis={selectedChart()[0]}
                      yaxis={selectedChart()[1]}
                      taxis={selectedChart()[2]}
                      filters={selectedChart()[3]}
                      aggregates={getFilteredAggregates()}
                      aggregatesAVG={getFilteredAggregatesAVG()}
                      aggregatesSTD={getFilteredAggregatesSTD()}
                      aggregatesAAV={getFilteredAggregatesAAV()}
                      xType={selectedChart()[9] ?? selectedChart()[6] ?? 'AVG'}
                      yType={selectedChart()[10] ?? selectedChart()[6] ?? 'AVG'}
                      cloud={(selectedChart()[6] || 'AVG').toUpperCase() === 'AVG' ? filteredCloudValue : []}
                      targets={getFilteredTargets()}
                      targetName={targets()?.name || ''}
                      targetColor={targetColor()}
                      baselines={baselineOverlays()}
                      uwDw={UwDw()}
                      aggregate={selectedChart()[6] || 'AVG'}
                      color={color()}
                      groups={groups()}
                      mouseID={mouseID()}
                      highlights={[]}
                      setMouseID={setMouseID}
                      handleZoom={handleZoom}
                      zoom={zoom()}
                      selectedEvents={selectedAggregateEvents()}
                      hasCutData={cutEvents().length > 0}
                      onChartRendered={() => requestAnimationFrame(() => setModeSwitching(false))}
                      infoType={selectedChart()[7]}
                      infoMessage={selectedChart()[8] ?? ""}
                      onLocalSelectionAssign={onLocalPerformanceSelectionAssign}
                    />
                  </div>
                </div>
              </Show>
              </div>
            </div>
            <div
              id="tt"
              class="tooltip"
              style={{
                opacity: tooltip().visible ? 1 : 0,
                left: `${tooltip().x}px`,
                top: `${tooltip().y}px`,
                position: "absolute",
                "pointer-events": "none",
              }}
              innerHTML={sanitizeTooltipHtml(tooltip().content)}
            ></div>
          </div>
        </Show>
      </Show>

      <TargetSelectionModal
        show={showTargetModal()}
        onClose={() => setShowTargetModal(false)}
        availableTargets={availableTargets()}
        selectedTargetName={selectedTargetName()}
        onSelectedTargetNameChange={setSelectedTargetName}
        targetColor={modalTargetColor()}
        onTargetColorChange={setModalTargetColor}
        onConfirm={handleTargetSelect}
      />

      {/* One picker per slot. Only the slot whose legend entry was clicked has show() true. */}
      <For each={baselineSlots}>
        {(slot) => (
          <BaselineDisplayModal
            show={slot.showModal()}
            onClose={slot.closeModal}
            title={`Select Baseline ${slot.slot}`}
            baselines={slot.availableBaselines()}
            selectedKey={slot.selectedKey()}
            onSelectedKeyChange={slot.setSelectedKey}
            configs={slot.modalConfigs()}
            selectedConfig={slot.modalConfig()}
            onSelectedConfigChange={slot.setModalConfig}
            baselineColor={slot.modalColor()}
            onBaselineColorChange={slot.setModalColor}
            loading={slot.loading()}
            loadingAction={slot.loadingAction()}
            error={slot.error()}
            onConfirm={() => void slot.handleConfirm()}
            onRemove={() => void slot.handleRemove()}
            onExport={() => void baselineExport.handleExport(slot)}
            exporting={isExportingSlot(slot.slot)}
          />
        )}
      </For>
    </div>
  );
}
